import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useAdmin } from '../context/AdminContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import EditProductModal from '../components/EditProductModal';
import AvailableProducts from '../components/AvailableProducts';
import { adminFetchInit } from '../utils/adminToken';

// test
// Security function to sanitize user input and prevent XSS
const sanitizeText = (text) => {
  if (!text) return '';
  
  // Convert to string and remove any HTML tags and scripts
  const cleanText = String(text)
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '[BLOCKED SCRIPT]')
    .replace(/<[^>]*>/g, '') // Remove all HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: protocols
    .replace(/on\w+\s*=/gi, '') // Remove event handlers
    .trim();
    
  return cleanText;
};

// Additional security for displaying user content safely
const SecureText = ({ children, className = '', title = '' }) => {
  const sanitizedText = sanitizeText(children);
  const sanitizedTitle = sanitizeText(title);
  
  return (
    <span 
      className={className} 
      title={sanitizedTitle}
      dangerouslySetInnerHTML={undefined} // Prevent any HTML injection
    >
      {sanitizedText}
    </span>
  );
};

const AdminDashboard = () => {
  const { admin, isAuthenticated, isLoading } = useAdmin();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // Helper function to get website based on admin owner
  const getWebsite = useCallback(() => {
    if (!admin?.owner) return null;
    // Don't filter for admin user (maintains global access)
    if (admin.owner === 'admin') return null;
    // For streamer admins, append .com to owner to match upload table website field
    return `${admin.owner}.com`;
  }, [admin?.owner]);
  
  // Read URL parameter immediately for initial state
  const getInitialTab = () => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam && ['challenges', 'validation', 'statistics', 'archives'].includes(tabParam)) {
      return tabParam;
    }
    return 'challenges'; // default
  };

  // Calculate initial sidebar state from tab
  const getInitialSidebarItem = (tab) => {
    switch (tab) {
      case 'challenges':
        return 'dashboard';
      case 'validation':
        return 'soumissions';
      case 'statistics':
        return 'votes';
      default:
        return 'dashboard';
    }
  };


  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [pendingUploads, setPendingUploads] = useState([]);
  const [loadingUploads, setLoadingUploads] = useState(false);
  const [processingUpload, setProcessingUpload] = useState(null);
  const [processingAction, setProcessingAction] = useState(null); // 'approve' or 'reject'
  const [showRejectDropdown, setShowRejectDropdown] = useState(null); // Track which upload shows dropdown
  const [rejectHoverTimeout, setRejectHoverTimeout] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Status filter state for validation section
  const [statusFilter, setStatusFilter] = useState('all'); // Changed from 'pending' to 'all' to show all uploads by default

  // Archives tab state
  const [archiveUploads, setArchiveUploads] = useState([]);
  const [loadingArchives, setLoadingArchives] = useState(false);
  const [archiveDateFrom, setArchiveDateFrom] = useState('');
  const [archiveDateTo, setArchiveDateTo] = useState('');
  const [archiveStatusFilter, setArchiveStatusFilter] = useState('1'); // '1' = approved, '4' = archived
  const [selectedArchiveUploads, setSelectedArchiveUploads] = useState(new Set());
  const [processingArchiveAction, setProcessingArchiveAction] = useState(false);

  // Archive folder state
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveModalName, setArchiveModalName] = useState('');
  const [archiveModalDescription, setArchiveModalDescription] = useState('');
  const [archiveModalAction, setArchiveModalAction] = useState(null); // 'new' or existing archive id
  const [archiveFolders, setArchiveFolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null); // null = show folders, object = show folder contents
  const [folderUploads, setFolderUploads] = useState([]);
  const [loadingFolderUploads, setLoadingFolderUploads] = useState(false);
  const [showFolderView, setShowFolderView] = useState(false); // toggle between list view and folder view
  const [existingArchives, setExistingArchives] = useState([]); // for the modal dropdown

  // Edit product modal state
  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [loadingEditProduct, setLoadingEditProduct] = useState(false);

  // Challenge creation form state
  const [challengeForm, setChallengeForm] = useState({
    name: '',
    startDate: new Date().toISOString().split('T')[0], // Default to today's date
    endDate: '',
    thematic: 'Nourriture',
    color: '#06b6d4', // Default cyan color
    description: '',
    rewards: {
      first: ['', ''],
      second: ['', ''],
      third: ['', '']
    }
  });
  const [isSubmittingChallenge, setIsSubmittingChallenge] = useState(false);
  const [challengeError, setChallengeError] = useState('');
  const [challengeSuccess, setChallengeSuccess] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingChallengeId, setEditingChallengeId] = useState(null);
  
  // Toast notification state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success'); // 'success' or 'error'

  // Appeal modal state
  const [showAppealModal, setShowAppealModal] = useState(false);
  const [appealMessage, setAppealMessage] = useState('');
  const [isSendingAppeal, setIsSendingAppeal] = useState(false);

  // Active challenges state
  const [activeChallenges, setActiveChallenges] = useState([]);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [loadingChallenges, setLoadingChallenges] = useState(false);
  const [showChallengeDropdown, setShowChallengeDropdown] = useState(false);

  // Sidebar active state - Calculate from initial tab
  const [activeSidebarItem, setActiveSidebarItem] = useState(getInitialSidebarItem(getInitialTab()));

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [challengeToDelete, setChallengeToDelete] = useState(null);

  // End challenge confirmation modal state
  const [showEndChallengeModal, setShowEndChallengeModal] = useState(false);
  const [challengeToEnd, setChallengeToEnd] = useState(null);

  // Delete designs confirmation modal state
  const [showDeleteDesignsModal, setShowDeleteDesignsModal] = useState(false);

  // Image modal state
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageBlur, setSelectedImageBlur] = useState(null);
  const [revealBlurredImage, setRevealBlurredImage] = useState(false);
  const [designsToDelete, setDesignsToDelete] = useState([]);

  // Pagination state for validation section
  const [validationCurrentPage, setValidationCurrentPage] = useState(1);
  const [validationDesignsPerPage, setValidationDesignsPerPage] = useState(12);

  // Selected uploads state for checkboxes
  const [selectedUploads, setSelectedUploads] = useState(new Set());

  // Publish modal state
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [selectedUploadForPublish, setSelectedUploadForPublish] = useState(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isUnpublishing, setIsUnpublishing] = useState(false);
  const [unpublishingUploadId, setUnpublishingUploadId] = useState(null);
  const [showUnpublishModal, setShowUnpublishModal] = useState(false);
  const [uploadToUnpublish, setUploadToUnpublish] = useState(null);

  // Calculate pagination data for validation section
  const validationTotalPages = Math.ceil(pendingUploads.length / validationDesignsPerPage);
  const validationStartIndex = (validationCurrentPage - 1) * validationDesignsPerPage;
  const validationEndIndex = validationStartIndex + validationDesignsPerPage;
  const currentValidationUploads = pendingUploads.slice(validationStartIndex, validationEndIndex);

  // Pagination handlers for validation section
  const goToValidationPage = (page) => {
    setValidationCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToValidationPreviousPage = () => {
    if (validationCurrentPage > 1) {
      goToValidationPage(validationCurrentPage - 1);
    }
  };

  const goToValidationNextPage = () => {
    if (validationCurrentPage < validationTotalPages) {
      goToValidationPage(validationCurrentPage + 1);
    }
  };

  // Redirect if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Redirect admin users to their own dashboard
  useEffect(() => {
    if (!isLoading && isAuthenticated && admin?.owner === 'admin') {
      console.log('🚫 Admin trying to access streamer dashboard, redirecting to admin dashboard');
      navigate('/admin/dashboard');
    }
  }, [admin, isAuthenticated, isLoading, navigate]);

  // Handle URL changes (browser back/forward)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam && ['challenges', 'validation', 'statistics', 'archives'].includes(tabParam)) {
      setActiveTab(tabParam);
      // Immediately update sidebar state to prevent blinking
      setActiveSidebarItem(getInitialSidebarItem(tabParam));
    } else if (!tabParam) {
      setActiveTab('challenges'); // default when no tab param
      setActiveSidebarItem('dashboard');
    }
  }, [location.search]);

  // Handle hash navigation for scrolling to sections
  useEffect(() => {
    if (location.hash) {
      const hashId = location.hash.substring(1);
      
      // If navigating to create-challenge, make sure we're on the challenges tab
      if (hashId === 'create-challenge' && activeTab !== 'challenges') {
        setActiveTab('challenges');
        setActiveSidebarItem('dashboard');
      }
      
      // Use a longer timeout and retry mechanism to ensure element is rendered
      const scrollToElement = (attempts = 0) => {
        const element = document.getElementById(hashId);
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        } else if (attempts < 10) {
          // Retry up to 10 times with increasing delay
          setTimeout(() => scrollToElement(attempts + 1), 200 + (attempts * 100));
        }
      };
      
      // Initial delay to ensure DOM is rendered
      setTimeout(() => scrollToElement(), 300);
    }
  }, [location.hash, activeTab]); // Re-run when hash or tab changes



  const fetchPendingUploads = useCallback(async () => {
    setLoadingUploads(true);
    try {
      // Build query string based on status filter and website
      let url = '/.netlify/functions/get-pending-uploads';
      const params = new URLSearchParams();
      // Named filter avoids ?status=0 being dropped or mishandled by some proxies; maps on server to 9,0,1,2
      params.append('filter', statusFilter);
      
      // Add website filtering for streamer admins
      const website = getWebsite();
      if (website) {
        params.append('website', website);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      console.log('📡 Fetching uploads with URL:', url, 'Status filter:', statusFilter);
      
      const response = await fetch(url, adminFetchInit());
      const result = await response.json();
      
      if (result.success) {
        setPendingUploads(result.data || []);
        console.log('Fetched uploads with status filter:', statusFilter, result.data);
      } else {
        console.error('Failed to fetch uploads:', result.message);
      }
    } catch (error) {
      console.error('Error fetching uploads:', error);
    } finally {
      setLoadingUploads(false);
    }
  }, [statusFilter, getWebsite]);

  // Fetch pending uploads when validation tab is active or statusFilter changes
  useEffect(() => {
    if (activeTab === 'validation' && isAuthenticated) {
      fetchPendingUploads();
    }
  }, [activeTab, isAuthenticated, fetchPendingUploads]);

  // Fetch pending uploads count on component mount for sidebar badge
  useEffect(() => {
    if (isAuthenticated) {
      fetchPendingUploads();
    }
  }, [isAuthenticated, fetchPendingUploads]);

  // Fetch archive uploads function
  const fetchArchiveUploads = useCallback(async () => {
    setLoadingArchives(true);
    try {
      let url = '/.netlify/functions/get-pending-uploads';
      const params = new URLSearchParams();
      
      // Filter by status (approved = 1 or archived = 4)
      params.append('status', archiveStatusFilter);
      
      // Add website filtering for streamer admins
      const website = getWebsite();
      if (website) {
        params.append('website', website);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      console.log('📡 Fetching archive uploads with URL:', url);
      
      const response = await fetch(url, adminFetchInit());
      const result = await response.json();
      
      if (result.success) {
        let filteredData = result.data || [];
        
        // Apply date filters if provided (using submitted_at)
        if (archiveDateFrom) {
          const fromDate = new Date(archiveDateFrom);
          fromDate.setHours(0, 0, 0, 0);
          filteredData = filteredData.filter(upload => {
            const uploadDate = new Date(upload.submitted_at || upload.created_at);
            return uploadDate >= fromDate;
          });
        }
        
        if (archiveDateTo) {
          const toDate = new Date(archiveDateTo);
          toDate.setHours(23, 59, 59, 999);
          filteredData = filteredData.filter(upload => {
            const uploadDate = new Date(upload.submitted_at || upload.created_at);
            return uploadDate <= toDate;
          });
        }
        
        setArchiveUploads(filteredData);
        console.log('Fetched archive uploads:', filteredData);
      } else {
        console.error('Failed to fetch archive uploads:', result.message);
      }
    } catch (error) {
      console.error('Error fetching archive uploads:', error);
    } finally {
      setLoadingArchives(false);
    }
  }, [archiveStatusFilter, archiveDateFrom, archiveDateTo, getWebsite]);

  // Fetch archive folders
  const fetchArchiveFolders = useCallback(async () => {
    setLoadingFolders(true);
    try {
      const website = getWebsite();
      const params = new URLSearchParams();
      if (website) params.append('website', website);
      const response = await fetch(`/.netlify/functions/get-archives?${params.toString()}`, adminFetchInit());
      const result = await response.json();
      if (result.success) {
        setArchiveFolders(result.data || []);
        setExistingArchives(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching archive folders:', error);
    } finally {
      setLoadingFolders(false);
    }
  }, [getWebsite]);

  // Fetch uploads for a specific archive folder
  const fetchFolderUploads = useCallback(async (archiveId) => {
    setLoadingFolderUploads(true);
    try {
      const website = getWebsite();
      const params = new URLSearchParams();
      params.append('archive_id', archiveId);
      if (website) params.append('website', website);
      const response = await fetch(`/.netlify/functions/get-archives?${params.toString()}`, adminFetchInit());
      const result = await response.json();
      if (result.success) {
        setFolderUploads(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching folder uploads:', error);
    } finally {
      setLoadingFolderUploads(false);
    }
  }, [getWebsite]);

  // Fetch archive uploads when archives tab is active or filters change
  useEffect(() => {
    if (activeTab === 'archives' && isAuthenticated) {
      fetchArchiveUploads();
      fetchArchiveFolders();
    }
  }, [activeTab, isAuthenticated, fetchArchiveUploads, fetchArchiveFolders]);

  // Open archive modal (instead of directly archiving)
  const openArchiveModal = () => {
    if (selectedArchiveUploads.size === 0) {
      showToastMessage('Please select at least one upload', 'error');
      return;
    }
    setArchiveModalName('');
    setArchiveModalDescription('');
    setArchiveModalAction('new');
    setShowArchiveModal(true);
  };

  // Submit archive action from modal
  const submitArchiveAction = async () => {
    if (archiveModalAction === 'new' && !archiveModalName.trim()) {
      showToastMessage('Please enter an archive name', 'error');
      return;
    }

    setProcessingArchiveAction(true);
    setShowArchiveModal(false);

    let successCount = 0;
    let failedCount = 0;
    let createdArchiveId = archiveModalAction !== 'new' ? parseInt(archiveModalAction, 10) : null;

    try {
      const uploadIds = Array.from(selectedArchiveUploads);

      for (let i = 0; i < uploadIds.length; i++) {
        const requestBody = {
          uploadId: uploadIds[i],
          status: 'archive',
          streamer_id: admin?.id || null
        };

        // First upload creates the archive folder, rest reuse the id
        if (archiveModalAction === 'new' && !createdArchiveId) {
          requestBody.archiveName = archiveModalName.trim();
          requestBody.archiveDescription = archiveModalDescription.trim();
        } else {
          requestBody.archive_id = createdArchiveId;
        }

        const website = getWebsite();
        if (website) requestBody.website = website;

        const response = await fetch('/.netlify/functions/update-upload-status', adminFetchInit({
          method: 'POST',
          body: JSON.stringify(requestBody),
        }));

        const result = await response.json();
        if (result.success) {
          successCount++;
          // Capture the archive_id from the first created upload for subsequent ones
          if (!createdArchiveId && result.data?.archive_id) {
            createdArchiveId = result.data.archive_id;
          }
        } else {
          failedCount++;
          console.error(`Failed to archive upload ${uploadIds[i]}:`, result.message);
        }
      }

      setSelectedArchiveUploads(new Set());

      if (successCount > 0 && failedCount === 0) {
        showToastMessage(`Successfully archived ${successCount} upload(s)`, 'success');
      } else if (successCount > 0) {
        showToastMessage(`Archived ${successCount}, failed ${failedCount}`, 'error');
      } else {
        showToastMessage('Failed to archive uploads', 'error');
      }

      // Redirect into the archive folder first, then refresh data in background
      if (createdArchiveId && successCount > 0) {
        const folderName = archiveModalAction === 'new'
          ? archiveModalName.trim()
          : (existingArchives.find(a => String(a.id) === String(createdArchiveId))?.name || 'Archive');
        const folderDesc = archiveModalAction === 'new'
          ? archiveModalDescription.trim()
          : (existingArchives.find(a => String(a.id) === String(createdArchiveId))?.description || '');
        setShowFolderView(true);
        setSelectedFolder({ id: createdArchiveId, name: folderName, description: folderDesc });
        await fetchFolderUploads(createdArchiveId);
      }

      // Refresh lists in the background (won't cause flash since we're in folder view now)
      fetchArchiveUploads();
      fetchArchiveFolders();
    } catch (error) {
      console.error('Error archiving uploads:', error);
      showToastMessage('Error archiving uploads', 'error');
    } finally {
      setProcessingArchiveAction(false);
    }
  };

  // Handle restore action for selected uploads
  const handleRestoreAction = async () => {
    if (selectedArchiveUploads.size === 0) {
      showToastMessage('Please select at least one upload', 'error');
      return;
    }

    setProcessingArchiveAction(true);
    let successCount = 0;
    let failedCount = 0;

    try {
      for (const uploadId of selectedArchiveUploads) {
        const requestBody = {
          uploadId,
          status: 'approved'
        };

        const website = getWebsite();
        if (website) requestBody.website = website;

        const response = await fetch('/.netlify/functions/update-upload-status', adminFetchInit({
          method: 'POST',
          body: JSON.stringify(requestBody),
        }));

        const result = await response.json();
        if (result.success) {
          successCount++;
        } else {
          failedCount++;
        }
      }

      await fetchArchiveUploads();
      await fetchArchiveFolders();
      setSelectedArchiveUploads(new Set());

      if (selectedFolder) {
        await fetchFolderUploads(selectedFolder.id);
      }

      if (successCount > 0 && failedCount === 0) {
        showToastMessage(`Successfully restored ${successCount} upload(s)`, 'success');
      } else if (successCount > 0) {
        showToastMessage(`Restored ${successCount}, failed ${failedCount}`, 'error');
      } else {
        showToastMessage('Failed to restore uploads', 'error');
      }
    } catch (error) {
      console.error('Error restoring uploads:', error);
      showToastMessage('Error restoring uploads', 'error');
    } finally {
      setProcessingArchiveAction(false);
    }
  };

  // Delete archive folder (restores all uploads inside, then deletes the folder)
  const [deletingFolder, setDeletingFolder] = useState(null);

  const handleDeleteFolder = async (folder) => {
    setDeletingFolder(folder);
  };

  const confirmDeleteFolder = async () => {
    if (!deletingFolder) return;
    setProcessingArchiveAction(true);
    setDeletingFolder(null);

    try {
      const response = await fetch('/.netlify/functions/get-archives', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({ action: 'delete', archive_id: deletingFolder.id }),
      }));

      const result = await response.json();
      if (result.success) {
        showToastMessage(`Folder deleted, ${result.restoredCount} design(s) restored`, 'success');
        setSelectedFolder(null);
        await fetchArchiveFolders();
        await fetchArchiveUploads();
      } else {
        showToastMessage('Failed to delete folder: ' + result.message, 'error');
      }
    } catch (error) {
      console.error('Error deleting folder:', error);
      showToastMessage('Error deleting folder', 'error');
    } finally {
      setProcessingArchiveAction(false);
    }
  };

  // Toggle select all archives
  const toggleSelectAllArchives = () => {
    if (selectedArchiveUploads.size === archiveUploads.length) {
      setSelectedArchiveUploads(new Set());
    } else {
      setSelectedArchiveUploads(new Set(archiveUploads.map(u => u.id)));
    }
  };

  // Toggle individual archive selection
  const toggleArchiveSelection = (uploadId) => {
    setSelectedArchiveUploads(prev => {
      const newSet = new Set(prev);
      if (newSet.has(uploadId)) {
        newSet.delete(uploadId);
      } else {
        newSet.add(uploadId);
      }
      return newSet;
    });
  };

  // Fetch active challenges on component mount
  const fetchActiveChallenges = useCallback(async () => {
    setLoadingChallenges(true);
    try {
      // Build URL with website filtering
      let url = '/.netlify/functions/get-challenges?activeOnly=true&sortBy=expiration';
      const website = getWebsite();
      if (website) {
        url += `&website=${website}`;
      }
      
      const response = await fetch(url);
      const result = await response.json();
      
      if (result.success) {
        const challenges = result.data || [];
        setActiveChallenges(challenges);
        if (challenges.length > 0) {
          setSelectedChallenge(challenges[0]);
          console.log('Auto-selected top challenge:', challenges[0]);
          console.log('Challenge rewards:', challenges[0].rewards);
          console.log('Rewards type:', typeof challenges[0].rewards);
        } else {
          setSelectedChallenge(null);
        }
        console.log('Fetched active challenges:', challenges);
      } else {
        console.error('Failed to fetch active challenges:', result.message);
      }
    } catch (error) {
      console.error('Error fetching active challenges:', error);
    } finally {
      setLoadingChallenges(false);
    }
  }, [getWebsite]);

  useEffect(() => {
    fetchActiveChallenges();
  }, [fetchActiveChallenges]);



  // Reset validation pagination when pending uploads change
  useEffect(() => {
    setValidationCurrentPage(1);
  }, [pendingUploads.length]);

  // Sync sidebar active state with active tab
  useEffect(() => {
    switch (activeTab) {
      case 'challenges':
        setActiveSidebarItem('dashboard');
        break;
      case 'validation':
        setActiveSidebarItem('soumissions');
        break;
      case 'statistics':
        setActiveSidebarItem('votes');
        break;
      default:
        setActiveSidebarItem('dashboard');
    }
  }, [activeTab]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showChallengeDropdown && !event.target.closest('[data-dropdown="active-challenges"]')) {
        setShowChallengeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showChallengeDropdown]);

  const handleUploadAction = async (uploadId, action, reason = null) => {
    setProcessingUpload(uploadId);
    setProcessingAction(action === 'approved' ? 'approve' : 'reject');
    setShowRejectDropdown(null); // Hide dropdown when processing
    
    // Find the upload to check for blur and get email
    const uploadData = pendingUploads.find(u => u.id === uploadId);
    
    try {
      const requestBody = {
        uploadId,
        status: action,
        adminEmail: admin.email
      };
      
      // Include reason if it's a rejection
      if (action === 'rejected' && reason) {
        requestBody.reason = reason;
      }

      // Add website filtering for streamer admins
      const website = getWebsite();
      if (website) {
        requestBody.website = website;
      }
      
      const response = await fetch('/.netlify/functions/update-upload-status', adminFetchInit({
        method: 'POST',
        body: JSON.stringify(requestBody),
      }));

      const result = await response.json();

      if (result.success) {
        console.log(`Upload ${action} successfully`);
        
        // Send blur notification email if image has blur and is being approved
        if (action === 'approved' && uploadData?.blur && uploadData?.email) {
          try {
            console.log(`📧 Sending blur notification to: ${uploadData.email}`);
            await fetch('/.netlify/functions/send-blur-notification', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                creatorEmail: uploadData.email,
                creatorName: uploadData.author || `${uploadData.first_name || ''} ${uploadData.last_name || ''}`.trim(),
                designTitle: uploadData.title,
                blurReason: uploadData.blur,
                streamerName: admin?.owner || 'PimpMyDrop',
                language: 'en'
              })
            });
            console.log('✅ Blur notification email sent');
          } catch (emailError) {
            console.error('❌ Failed to send blur notification email:', emailError);
            // Don't fail the approval if email fails
          }
        }
        
        if (statusFilter === 'all') {
          // For "all" status filter, update the upload status locally to keep it visible
          setPendingUploads(prev => prev.map(upload => 
            upload.id === uploadId 
              ? { ...upload, status: action === 'approved' ? 1 : 2 }
              : upload
          ));
        } else {
          // For specific status filters (pending, approved, rejected), remove the item since it no longer matches the filter
          setPendingUploads(prev => prev.filter(upload => upload.id !== uploadId));
        }
        
        showToastMessage(action === 'approved' ? t('uploadApprovedSuccess') : t('uploadRejectedSuccess'), 'success');
      } else {
        console.error(`Failed to ${action} upload:`, result.message);
        alert(`Erreur: ${result.message}`);
      }
    } catch (error) {
      console.error(`Error ${action}ing upload:`, error);
      alert(`Erreur lors de la ${action === 'approved' ? 'validation' : 'rejection'}`);
    } finally {
      setProcessingUpload(null);
      setProcessingAction(null);
    }
  };

  // Handle mouse enter for reject button
  const handleRejectHover = (uploadId) => {
    if (rejectHoverTimeout) {
      clearTimeout(rejectHoverTimeout);
    }
    const timeout = setTimeout(() => {
      setShowRejectDropdown(uploadId);
    }, 500); // 500ms delay before showing dropdown
    setRejectHoverTimeout(timeout);
  };

  // Handle mouse leave for reject button and dropdown
  const handleRejectLeave = () => {
    if (rejectHoverTimeout) {
      clearTimeout(rejectHoverTimeout);
    }
    const timeout = setTimeout(() => {
      setShowRejectDropdown(null);
    }, 200); // 200ms delay before hiding dropdown
    setRejectHoverTimeout(timeout);
  };

  // Handle rejection with reason
  const handleRejectWithReason = (uploadId, reason) => {
    handleUploadAction(uploadId, 'rejected', reason);
  };

  // Handle publish upload - show modal
  const handlePublishUpload = (upload) => {
    setSelectedUploadForPublish(upload);
    setShowPublishModal(true);
  };

  // Handle publish modal close
  const handlePublishModalClose = () => {
    setShowPublishModal(false);
    setSelectedUploadForPublish(null);
  };

  // Handle edit product — load full Shopify product (same source as Admin Store) for EditProductModal
  const handleEditProduct = async (upload) => {
    const shopifyData = upload.shopify_info || {};
    const shopifyId =
      shopifyData.shopifyId != null ? String(shopifyData.shopifyId).trim() : '';

    if (!shopifyId) {
      showToastMessage('No Shopify product linked to this upload', 'error');
      return;
    }

    setLoadingEditProduct(true);
    try {
      const response = await fetch(
        `/.netlify/functions/shopify-products?product_id=${encodeURIComponent(shopifyId)}`,
        adminFetchInit()
      );
      const data = await response.json().catch(() => ({}));

      if (data.success && data.product) {
        const merged = {
          ...data.product,
          products: upload.products ?? data.product.products,
        };
        setEditingProduct(merged);
        setShowEditProductModal(true);
        return;
      }

      throw new Error(data.error || data.message || `Could not load product (${response.status})`);
    } catch (err) {
      console.error('Edit product load failed:', err);
      showToastMessage(err.message || 'Could not load product from Shopify', 'error');
    } finally {
      setLoadingEditProduct(false);
    }
  };

  // Handle edit product modal close
  const handleEditProductModalClose = () => {
    setShowEditProductModal(false);
    setEditingProduct(null);
    setLoadingEditProduct(false);
  };

  // Handle product updated — mirror Admin Store: update local uploads + persist shopify_info
  const handleProductUpdated = async (updatedProduct) => {
    const sid = String(updatedProduct.shopifyId ?? updatedProduct.id ?? '').trim();

    let uploadIdForDb = null;
    setPendingUploads((prev) =>
      prev.map((u) => {
        const usid =
          u.shopify_info?.shopifyId != null ? String(u.shopify_info.shopifyId).trim() : '';
        if (!usid || usid !== sid) return u;
        uploadIdForDb = u.id;
        return {
          ...u,
          shopify_info: {
            ...(u.shopify_info || {}),
            shopifyId: updatedProduct.shopifyId ?? updatedProduct.id,
            shopifyPrice: updatedProduct.price,
            shopifyStock: updatedProduct.stock,
            shopifyHandle: updatedProduct.handle ?? u.shopify_info?.shopifyHandle,
            shopifyStatus: updatedProduct.status ?? u.shopify_info?.shopifyStatus,
            updatedAt: new Date().toISOString(),
          },
        };
      })
    );

    if (uploadIdForDb != null) {
      await updateShopifyInfoInDatabase(uploadIdForDb, updatedProduct);
    }

    showToastMessage(`Product "${updatedProduct.name}" updated successfully!`, 'success');
  };

  // Update shopify_info in database
  const updateShopifyInfoInDatabase = async (uploadId, updatedProduct) => {
    try {
      console.log('💾 Updating shopify_info in database for upload:', uploadId);
      
      // Prepare updated Shopify info
      const updatedShopifyInfo = {
        shopifyId: updatedProduct.id,
        shopifyHandle: updatedProduct.handle,
        shopifyStatus: updatedProduct.status,
        shopifyPrice: updatedProduct.price,
        shopifyStock: updatedProduct.stock,
        shopifyImage: updatedProduct.image,
        apiType: updatedProduct.apiType || 'admin-rest',
        apiVersion: updatedProduct.apiVersion || '2025-04',
        updatedAt: new Date().toISOString()
      };

      const requestBody = {
        uploadId,
        shopify_info: updatedShopifyInfo
      };

      // Add website filtering for streamer admins
      const website = getWebsite();
      if (website) {
        requestBody.website = website;
      }

      const response = await fetch('/.netlify/functions/update-upload-status', adminFetchInit({
        method: 'POST',
        body: JSON.stringify(requestBody),
      }));

      const result = await response.json();

      if (result.success) {
        console.log('✅ Shopify info updated in database successfully');
      } else {
        console.error('❌ Failed to update shopify_info in database:', result.error);
      }
    } catch (error) {
      console.error('❌ Error updating shopify_info in database:', error);
    }
  };

  // Handle actual publishing to Shopify


  const updatePublishStatus = async (uploadId, shopifyInfo = null) => {
    try {
      const requestBody = {
        uploadId,
        publish: 1
      };

      // Add website filtering for streamer admins
      const website = getWebsite();
      if (website) {
        requestBody.website = website;
      }

      // Add Shopify product info if provided
      if (shopifyInfo) {
        requestBody.shopify_info = shopifyInfo;
      }

      const response = await fetch('/.netlify/functions/update-upload-status', adminFetchInit({
        method: 'POST',
        body: JSON.stringify(requestBody),
      }));

      const result = await response.json();

      if (result.success) {
        console.log('Publish status updated successfully');
        console.log('Updated result:', result);
        // Update local state to reflect the change
        setPendingUploads(prev => prev.map(upload => {
          if (upload.id === uploadId) {
            console.log('Updating upload in local state:', upload.id, 'publish:', 1);
            const updatedUpload = { ...upload, publish: 1 };
            if (shopifyInfo) {
              updatedUpload.shopify_info = shopifyInfo;
            }
            return updatedUpload;
          }
          return upload;
        }));
        
        // Force refresh to ensure UI updates
        setTimeout(() => {
          fetchPendingUploads();
        }, 1000);
      } else {
        console.error('Failed to update publish status:', result.error);
        showToastMessage(t('errorPublishStatusUpdate'), 'error');
      }
    } catch (error) {
      console.error('Error updating publish status:', error);
      showToastMessage('Erreur lors de la mise à jour du statut de publication', 'error');
    }
  };

  const handleShopifyPublish = async (productData) => {
    setIsPublishing(true);
    try {
      const response = await fetch('/.netlify/functions/shopify-create-product', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({ product: productData }),
      }));

      const result = await response.json();
      
      if (result.success) {
        // Build per-type variant pricing map from the Shopify response
        const shopifyVariants = (result.product.variants || []).map(v => ({
          id: v.id,
          option1: v.option1,
          option2: v.option2,
          price: v.price
        }));

        const shopifyInfo = {
          shopifyId: result.product.id,
          shopifyHandle: result.product.handle,
          shopifyStatus: result.product.status,
          shopifyPrice: result.product.price,
          shopifyStock: result.product.stock,
          shopifyImage: result.product.image,
          shopifyVariants,
          apiType: result.apiType,
          apiVersion: result.apiVersion,
          createdAt: result.product.createdAt,
          updatedAt: result.product.updatedAt
        };

        // Update publish status and store Shopify data
        console.log('🔄 Storing Shopify data for upload:', selectedUploadForPublish.id, shopifyInfo);
        await updatePublishStatus(selectedUploadForPublish.id, shopifyInfo);
        
        showToastMessage(t('productCreatedShopifySuccess'), 'success');
        setShowPublishModal(false);
        setSelectedUploadForPublish(null);
        // Refresh the uploads list
        fetchPendingUploads();
      } else {
        showToastMessage(result.error || t('errorCreatingProduct'), 'error');
      }
    } catch (error) {
      console.error('Error creating product:', error);
      showToastMessage(t('errorCreatingProduct'), 'error');
    } finally {
      setIsPublishing(false);
    }
  };

  // Handle unpublish - show confirmation modal
  const handleUnpublish = (upload) => {
    if (!upload || !upload.shopify_info?.shopifyId) {
      showToastMessage('No Shopify product data found', 'error');
      return;
    }
    setUploadToUnpublish(upload);
    setShowUnpublishModal(true);
  };

  // Confirm unpublish - delete from Shopify and clear shopify_info in DB
  const confirmUnpublish = async () => {
    const upload = uploadToUnpublish;
    if (!upload) return;

    setShowUnpublishModal(false);
    setIsUnpublishing(true);
    setUnpublishingUploadId(upload.id);
    try {
      // Step 1: Delete product from Shopify
      console.log('🗑️ Deleting product from Shopify:', upload.shopify_info.shopifyId);
      const deleteResponse = await fetch('/.netlify/functions/shopify-delete-product', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({ productId: upload.shopify_info.shopifyId }),
      }));

      const deleteResult = await deleteResponse.json();

      if (!deleteResult.success) {
        showToastMessage(`Failed to delete from Shopify: ${deleteResult.error}`, 'error');
        return;
      }

      console.log('✅ Product deleted from Shopify');

      // Step 2: Update database - set publish to 0 and clear shopify_info
      const requestBody = {
        uploadId: upload.id,
        publish: 0,
        shopify_info: null
      };

      const website = getWebsite();
      if (website) {
        requestBody.website = website;
      }

      const updateResponse = await fetch('/.netlify/functions/update-upload-status', adminFetchInit({
        method: 'POST',
        body: JSON.stringify(requestBody),
      }));

      const updateResult = await updateResponse.json();

      if (updateResult.success) {
        console.log('✅ Upload unpublished in database');
        showToastMessage('Product unpublished successfully', 'success');

        // Update local state
        setPendingUploads(prev => prev.map(u => {
          if (u.id === upload.id) {
            return { ...u, publish: 0, shopify_info: null };
          }
          return u;
        }));

        // Refresh uploads
        setTimeout(() => fetchPendingUploads(), 1000);
      } else {
        showToastMessage(`Product deleted from Shopify but failed to update database: ${updateResult.error}`, 'error');
      }
    } catch (error) {
      console.error('Error unpublishing:', error);
      showToastMessage(`Error unpublishing: ${error.message}`, 'error');
    } finally {
      setIsUnpublishing(false);
      setUnpublishingUploadId(null);
      setUploadToUnpublish(null);
    }
  };

  const cancelUnpublish = () => {
    setShowUnpublishModal(false);
    setUploadToUnpublish(null);
  };

  // Mobile menu handlers
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Challenge form handlers
  const handleChallengeFormChange = (field, value) => {
    setChallengeForm(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear errors when user starts typing
    if (challengeError) setChallengeError('');
  };

  const handleRewardChange = (position, index, value) => {
    setChallengeForm(prev => ({
      ...prev,
      rewards: {
        ...prev.rewards,
        [position]: prev.rewards[position].map((reward, i) => 
          i === index ? value : reward
        )
      }
    }));
  };

  const addReward = (position) => {
    setChallengeForm(prev => ({
      ...prev,
      rewards: {
        ...prev.rewards,
        [position]: [...prev.rewards[position], '']
      }
    }));
  };

  const removeReward = (position, index) => {
    setChallengeForm(prev => ({
      ...prev,
      rewards: {
        ...prev.rewards,
        [position]: prev.rewards[position].filter((_, i) => i !== index)
      }
    }));
  };

  const resetChallengeForm = () => {
    setChallengeForm({
      name: '',
      startDate: new Date().toISOString().split('T')[0], // Default to today's date
      endDate: '',
      thematic: 'Nourriture',
      color: '#06b6d4', // Default cyan color
      description: '',
      rewards: {
        first: ['', ''],
        second: ['', ''],
        third: ['', '']
      }
    });
    setChallengeError('');
    setChallengeSuccess('');
    setIsEditMode(false);
    setEditingChallengeId(null);
  };

  const handleChallengeSubmit = async () => {
    // Validate form
    if (!challengeForm.name.trim()) {
      setChallengeError('Le nom du challenge est requis');
      return;
    }
    if (!challengeForm.startDate) {
      setChallengeError('La date de début est requise');
      return;
    }
    if (!challengeForm.endDate) {
      setChallengeError('La date de fin est requise');
      return;
    }
    if (new Date(challengeForm.startDate) >= new Date(challengeForm.endDate)) {
      setChallengeError('La date de fin doit être après la date de début');
      return;
    }
    if (!challengeForm.description.trim()) {
      setChallengeError('La description est requise');
      return;
    }

    setIsSubmittingChallenge(true);
    setChallengeError('');

    try {
      // Prepare rewards data - filter out empty rewards
      const preparedRewards = {
        first: challengeForm.rewards.first.filter(reward => reward.trim() !== ''),
        second: challengeForm.rewards.second.filter(reward => reward.trim() !== ''),
        third: challengeForm.rewards.third.filter(reward => reward.trim() !== '')
      };

      const challengeData = {
        title: challengeForm.name.trim(),
        description: challengeForm.description.trim(),
        color: challengeForm.color,
        expiration: challengeForm.endDate, // End date becomes expiration
        startDate: challengeForm.startDate,
        thematic: challengeForm.thematic,
        rewards: preparedRewards
        // Admin-created challenges don't include userId field
      };

      // Add website filtering for streamer admins
      const website = getWebsite();
      if (website) {
        challengeData.website = website;
      }

      // Add challenge ID for updates
      if (isEditMode && editingChallengeId) {
        challengeData.id = editingChallengeId;
      }

      console.log(`${isEditMode ? 'Updating' : 'Creating'} challenge with data:`, challengeData);

      const response = await fetch('/.netlify/functions/challenge', {
        method: isEditMode ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(challengeData)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        const successMessage = isEditMode 
          ? t('challengeModifiedSuccess')
          : t('challengeCreatedSuccess');
        setChallengeSuccess(successMessage);
        showToastMessage(successMessage, 'success');
        resetChallengeForm();
        
        // Always refresh challenges list after create/update
        fetchActiveChallenges();
        
        // Reset edit mode after successful create/update
        setIsEditMode(false);
        setEditingChallengeId(null);
        
        // Also trigger a small delay to ensure UI updates properly
        setTimeout(() => {
          // Force a re-render of any challenge-dependent components
          console.log('🎉 Challenge created/updated - UI should now show the new challenge');
        }, 100);
        
        // Auto-hide success message after 5 seconds
        setTimeout(() => setChallengeSuccess(''), 5000);
      } else {
        const errorMessage = result.message || (isEditMode ? t('errorModifyingChallenge') : t('errorCreatingChallenge'));
        setChallengeError(errorMessage);
        showToastMessage(errorMessage, 'error');
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} challenge:`, error);
      const errorMessage = isEditMode ? t('errorModifyingChallenge') : t('errorCreatingChallenge');
      setChallengeError(errorMessage);
    } finally {
      setIsSubmittingChallenge(false);
    }
  };

  // Toast function
  const showToastMessage = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    
    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  };

  const handleChallengeSelect = (challenge) => {
    setSelectedChallenge(challenge);
    setShowChallengeDropdown(false);
    console.log('Selected challenge:', challenge);
  };

  const toggleChallengeDropdown = () => {
    setShowChallengeDropdown(!showChallengeDropdown);
  };

  const handleEditChallenge = () => {
    if (!selectedChallenge) return;
    
    // Parse rewards safely
    let parsedRewards = {
      first: ['', ''],
      second: ['', ''],
      third: ['', '']
    };
    
    if (selectedChallenge.rewards) {
      try {
        const rewards = typeof selectedChallenge.rewards === 'object' 
          ? selectedChallenge.rewards 
          : JSON.parse(selectedChallenge.rewards);
        
        if (rewards) {
          parsedRewards = {
            first: rewards.first && rewards.first.length > 0 ? rewards.first : ['', ''],
            second: rewards.second && rewards.second.length > 0 ? rewards.second : ['', ''],
            third: rewards.third && rewards.third.length > 0 ? rewards.third : ['', '']
          };
        }
      } catch (error) {
        console.error('Error parsing rewards for edit:', error);
      }
    }
    
    // Format dates for input fields
    const startDate = selectedChallenge.start_date 
      ? new Date(selectedChallenge.start_date).toISOString().split('T')[0] 
      : '';
    const endDate = selectedChallenge.expiration 
      ? new Date(selectedChallenge.expiration).toISOString().split('T')[0] 
      : '';
    
    // Populate form with challenge data
    setChallengeForm({
      name: selectedChallenge.title || '',
      startDate: startDate,
      endDate: endDate,
      thematic: selectedChallenge.thematic || 'Nourriture',
      color: selectedChallenge.color || '#06b6d4',
      description: selectedChallenge.description || '',
      rewards: parsedRewards
    });
    
    // Switch to edit mode
    setIsEditMode(true);
    setEditingChallengeId(selectedChallenge.id);
    
    // Clear any previous messages
    setChallengeError('');
    setChallengeSuccess('');
    
    console.log('Editing challenge:', selectedChallenge);
    
    // Scroll to the edit form section with smooth animation
    setTimeout(() => {
      const editSection = document.querySelector('[data-section="create-challenge"]');
      if (editSection) {
        editSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 100); // Small delay to ensure state updates are complete
  };

  const handleDeleteChallenge = async () => {
    if (!selectedChallenge) return;
    
    // Show delete confirmation modal instead of window.confirm
    setChallengeToDelete(selectedChallenge);
    setShowDeleteModal(true);
  };

  const confirmDeleteChallenge = async () => {
    if (!challengeToDelete) return;
    
    try {
      console.log('Deleting challenge:', challengeToDelete);
      
      const deleteData = {
        id: challengeToDelete.id
        // Admin operations don't include userId field
      };

      // Add website filtering for streamer admins
      const website = getWebsite();
      if (website) {
        deleteData.website = website;
      }

      const response = await fetch('/.netlify/functions/challenge', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(deleteData)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        showToastMessage(t('challengeDeletedSuccess'), 'success');
        
        // Refresh challenges list to remove deleted challenge
        fetchActiveChallenges();
        
        // Clear selected challenge if it was the deleted one
        if (selectedChallenge?.id === challengeToDelete.id) {
          setSelectedChallenge(null);
        }
        
        console.log('Challenge deleted successfully');
      } else {
        const errorMessage = result.message || t('errorDeletingChallenge');
        showToastMessage(errorMessage, 'error');
        console.error('Failed to delete challenge:', result);
      }
    } catch (error) {
      console.error('Error deleting challenge:', error);
      showToastMessage(t('errorDeletingChallenge'), 'error');
    } finally {
      // Close modal and reset state
      setShowDeleteModal(false);
      setChallengeToDelete(null);
    }
  };

  const cancelDeleteChallenge = () => {
    setShowDeleteModal(false);
    setChallengeToDelete(null);
  };

  const handleEndChallengeNow = async () => {
    if (!selectedChallenge) return;
    
    // Show confirmation modal instead of immediately ending
    setChallengeToEnd(selectedChallenge);
    setShowEndChallengeModal(true);
  };

  const confirmEndChallenge = async () => {
    if (!challengeToEnd) return;
    
    try {
      console.log('Ending challenge now:', challengeToEnd);
      
      // Set expiration time to current time (finished immediately)
      const newExpiration = new Date(); // Current time
      const newExpirationISO = newExpiration.toISOString();
      
      console.log('New expiration time:', newExpirationISO);
      
      const updateData = {
        id: challengeToEnd.id,
        title: challengeToEnd.title,
        description: challengeToEnd.description,
        color: challengeToEnd.color,
        expiration: newExpirationISO,
        startDate: challengeToEnd.start_date || challengeToEnd.created_at,
        thematic: challengeToEnd.thematic,
        rewards: challengeToEnd.rewards
        // Admin operations don't include userId field
      };

      // Add website filtering for streamer admins
      const website = getWebsite();
      if (website) {
        updateData.website = website;
      }

      const response = await fetch('/.netlify/functions/challenge', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        showToastMessage(t('challengeEndedSuccess'), 'success');
        
        // Refresh challenges list to get updated data
        fetchActiveChallenges();
        
        console.log('Challenge ended successfully');
      } else {
        const errorMessage = result.message || t('errorEndingChallenge');
        showToastMessage(errorMessage, 'error');
        console.error('Failed to end challenge:', result);
      }
    } catch (error) {
      console.error('Error ending challenge:', error);
      showToastMessage(t('errorEndingChallenge'), 'error');
    } finally {
      // Close modal and reset state
      setShowEndChallengeModal(false);
      setChallengeToEnd(null);
    }
  };

  const cancelEndChallenge = () => {
    setShowEndChallengeModal(false);
    setChallengeToEnd(null);
  };

  // Handler for status filter change
  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
    setPendingUploads([]);
    setLoadingUploads(true);
    setValidationCurrentPage(1); // Reset to first page when filter changes
    setSelectedUploads(new Set()); // Clear selections when filter changes
  };

  // Handler for display length change
  const handleDisplayLengthChange = (newLength) => {
    setValidationDesignsPerPage(parseInt(newLength));
    setValidationCurrentPage(1); // Reset to first page when display length changes
  };

  // Handler for checkbox selection
  const handleUploadSelection = (uploadId, isChecked) => {
    const newSelected = new Set(selectedUploads);
    if (isChecked) {
      newSelected.add(uploadId);
    } else {
      newSelected.delete(uploadId);
    }
    setSelectedUploads(newSelected);
  };

  // Handler for select all checkbox
  const handleSelectAll = () => {
    if (selectedUploads.size > 0) {
      // If any are selected, deselect all
      setSelectedUploads(new Set());
    } else {
      // If none are selected, select all current page uploads
      const allCurrentIds = new Set(currentValidationUploads.map(upload => upload.id));
      setSelectedUploads(allCurrentIds);
    }
  };

  // Handler for delete selected uploads
  const handleDeleteSelected = async () => {
    console.log('🔍 Delete Selected clicked!');
    console.log('🔍 Selected uploads:', selectedUploads);
    console.log('🔍 Selected uploads size:', selectedUploads.size);
    console.log('🔍 Admin email:', admin?.email);
    
    if (selectedUploads.size === 0) {
      console.log('❌ No uploads selected');
      showToastMessage(t('noDesignSelected'), 'error');
      return;
    }

    // Show confirmation modal instead of window.confirm
    const selectedIds = Array.from(selectedUploads);
    setDesignsToDelete(selectedIds);
    setShowDeleteDesignsModal(true);
  };

  const confirmDeleteDesigns = async () => {
    if (designsToDelete.length === 0) return;

    console.log('✅ User confirmed deletion, proceeding...');

    try {
      console.log('🔍 Selected IDs to delete:', designsToDelete);
      
      let successCount = 0;
      let failedUploads = [];
      
      // Process each selected upload
      for (const uploadId of designsToDelete) {
        console.log(`🔍 Deleting upload ID: ${uploadId}`);
        
        const requestBody = {
          uploadId,
          status: 'deleted', // Now supported by backend - maps to status 3
          adminEmail: admin?.email || 'admin'
        };

        // Add website filtering for streamer admins
        const website = getWebsite();
        if (website) {
          requestBody.website = website;
        }
        
        console.log('🔍 Request body:', requestBody);
        
        const response = await fetch('/.netlify/functions/update-upload-status', adminFetchInit({
          method: 'POST',
          body: JSON.stringify(requestBody),
        }));

        console.log('🔍 Response status:', response.status);
        
        const result = await response.json();
        console.log('🔍 Response result:', result);
        
        if (!result.success) {
          console.error(`❌ Failed to delete upload ${uploadId}:`, result.message);
          failedUploads.push(uploadId);
        } else {
          console.log(`✅ Successfully deleted upload ${uploadId}`);
          successCount++;
        }
      }

      console.log('🔍 All deletions completed, refreshing list...');
      
      // Refresh the uploads list and clear selections
      await fetchPendingUploads();
      setSelectedUploads(new Set());
      
      // Show appropriate success/error messages
      if (successCount > 0 && failedUploads.length === 0) {
        console.log('✅ Showing success toast');
        showToastMessage(t('designsDeletedSuccess').replace('{count}', successCount), 'success');
      } else if (successCount > 0 && failedUploads.length > 0) {
        showToastMessage(t('designsDeletedPartial').replace('{successCount}', successCount).replace('{failedCount}', failedUploads.length), 'error');
      } else {
        showToastMessage(t('errorDeletingAllDesigns'), 'error');
      }
    } catch (error) {
      console.error('❌ Error deleting selected uploads:', error);
      showToastMessage(t('errorDeletingDesigns'), 'error');
    } finally {
      // Close modal and reset state
      setShowDeleteDesignsModal(false);
      setDesignsToDelete([]);
    }
  };

  const cancelDeleteDesigns = () => {
    setShowDeleteDesignsModal(false);
    setDesignsToDelete([]);
  };

  // Handle sending appeal for blocked account
  const handleSendAppeal = async () => {
    if (!appealMessage.trim()) {
      showToastMessage('Please enter an appeal message', 'error');
      return;
    }

    setIsSendingAppeal(true);
    try {
      const response = await fetch('/.netlify/functions/send-appeal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          streamerName: admin?.owner || 'Unknown',
          streamerEmail: admin?.email || '',
          appealMessage: appealMessage
        })
      });

      const result = await response.json();

      if (result.success) {
        showToastMessage('Appeal sent successfully', 'success');
        setShowAppealModal(false);
        setAppealMessage('');
      } else {
        showToastMessage('Failed to send appeal: ' + result.message, 'error');
      }
    } catch (error) {
      console.error('Error sending appeal:', error);
      showToastMessage('Error sending appeal', 'error');
    } finally {
      setIsSendingAppeal(false);
    }
  };

  // Handle tab changes with URL updates
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/streamer/dashboard?tab=${tab}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  if (!admin) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="h-screen bg-gray-900 text-white flex overflow-hidden">
      {/* Sidebar - Now using reusable component */}
      <Sidebar 
        activeSidebarItem={activeSidebarItem}
        isMobileMenuOpen={isMobileMenuOpen}
        onCloseMobileMenu={closeMobileMenu}
        dashboardType="streamer"
      />

      {/* Mobile Backdrop Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={closeMobileMenu}
        />
      )}

      {/* Main Content - Scrollable */}
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {/* Header - Fixed */}
        <div className="bg-gray-800 p-[24px] flex justify-between items-center flex-shrink-0">
          <div className="flex items-center space-x-4">
            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden text-gray-400 hover:text-white p-2 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            
            <div>
              <h2 className="text-xl lg:text-2xl font-bold">{t('dashboard')}</h2>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 lg:space-x-4">
            {/* Account Status - Show if blocked */}
            {admin?.status === 'blocked' && (
              <div className="flex items-center space-x-3 bg-red-900/30 rounded-lg px-4">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span className="text-red-200 font-medium text-sm">Account Blocked</span>
                </div>
                <button
                  onClick={() => setShowAppealModal(true)}
                  className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-md transition-colors text-sm font-medium"
                  title="Send appeal to unblock your account"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>Send Appeal</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Content Area - Scrollable */}
        <div className="flex-1 overflow-y-auto" id="main-content-area">
          <div className="p-3 lg:p-6">

            {/* Bottom Section - Tabbed Interface */}
            <div className="bg-gray-800 rounded-xl">
              {/* Tab Headers */}
              <div className="flex border-b border-gray-700 overflow-x-auto">
                <button
                  onClick={() => handleTabChange('challenges')}
                  className={`px-4 lg:px-6 py-3 lg:py-4 font-medium transition-colors whitespace-nowrap text-sm lg:text-base ${
                    activeTab === 'challenges'
                      ? 'text-pink-500 border-b-2 border-pink-500 bg-gray-700'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  {t('currentChallenge')}
                </button>
                <button
                  onClick={() => handleTabChange('validation')}
                  className={`px-4 lg:px-6 py-3 lg:py-4 font-medium transition-colors whitespace-nowrap text-sm lg:text-base ${
                    activeTab === 'validation'
                      ? 'text-pink-500 border-b-2 border-pink-500 bg-gray-700'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  {t('visualValidation')}
                </button>
                <button
                  onClick={() => handleTabChange('statistics')}
                  className={`px-4 lg:px-6 py-3 lg:py-4 font-medium transition-colors whitespace-nowrap text-sm lg:text-base ${
                    activeTab === 'statistics'
                      ? 'text-pink-500 border-b-2 border-pink-500 bg-gray-700'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  {t('statistics')}
                </button>
                <button
                  onClick={() => handleTabChange('archives')}
                  className={`px-4 lg:px-6 py-3 lg:py-4 font-medium transition-colors whitespace-nowrap text-sm lg:text-base ${
                    activeTab === 'archives'
                      ? 'text-pink-500 border-b-2 border-pink-500 bg-gray-700'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  {t('archives')}
                </button>
              </div>

              {/* Tab Content */}
              <div className="p-3 lg:p-6">
                {/* Challenge en cours Tab */}
                {activeTab === 'challenges' && (
                  <div className="space-y-6">
                    {/* Current Challenge Section */}
                    <div className="bg-gray-700 rounded-xl p-6" data-section="current-challenge">
                      <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl font-bold text-yellow-400">{t('currentChallenges')} : {selectedChallenge ? selectedChallenge.title : t('noChallengeSelected')}</h2>
                        
                        {/* Active Challenges Dropdown */}
                        <div className="relative" data-dropdown="active-challenges">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={toggleChallengeDropdown}
                              className="flex items-center space-x-2 bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-lg transition-colors"
                              disabled={loadingChallenges}
                            >
                              <span className="text-sm">
                                {loadingChallenges ? t('loading') : 
                                 selectedChallenge ? selectedChallenge.title : t('selectChallenge')}
                              </span>
                              <svg className={`w-4 h-4 transition-transform ${showChallengeDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                              </svg>
                            </button>
                            
                            {/* Edit Icon */}
                            <button
                              className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg transition-colors"
                              title={t('editChallenge')}
                              disabled={!selectedChallenge}
                              onClick={handleEditChallenge}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                              </svg>
                            </button>
                            
                            {/* Trash Icon */}
                            <button
                              className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-lg transition-colors"
                              title={t('deleteChallenge')}
                              disabled={!selectedChallenge}
                              onClick={handleDeleteChallenge}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                              </svg>
                            </button>
                          </div>
                          
                          {showChallengeDropdown && (
                            <div className="absolute right-0 top-full mt-2 w-80 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
                              <div className="py-2">
                                {activeChallenges.length === 0 ? (
                                  <div className="px-4 py-3 text-sm text-gray-400 text-center">
                                    {loadingChallenges ? t('loadingChallenges') : t('noActiveChallengesFound')}
                                  </div>
                                ) : (
                                  activeChallenges.map((challenge) => (
                                    <button
                                      key={challenge.id}
                                      onClick={() => handleChallengeSelect(challenge)}
                                      className={`w-full text-left px-4 py-3 text-sm hover:bg-gray-700 transition-colors ${
                                        selectedChallenge?.id === challenge.id ? 'text-yellow-400 bg-gray-700' : 'text-white'
                                      }`}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                          <div className="font-medium">{challenge.title}</div>
                                          <div className="text-xs text-gray-400 mt-1">
                                            <span className="flex items-center space-x-1">
                                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                              </svg>
                                              <span>Expires: {challenge.expirationFormatted}</span>
                                            </span>
                                          </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                          {challenge.isExpiringSoon && (
                                            <span className="bg-red-600 text-white px-2 py-1 rounded-full text-xs font-medium">
                                              {challenge.daysLeft} day{challenge.daysLeft !== 1 ? 's' : ''}
                                            </span>
                                          )}
                                          {challenge.daysLeft > 3 && (
                                            <span className="bg-green-600 text-white px-2 py-1 rounded-full text-xs font-medium">
                                              {challenge.daysLeft} day{challenge.daysLeft !== 1 ? 's' : ''}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </button>
                                  ))
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                        {/* Challenge Details - 9 columns */}
                        <div className="lg:col-span-9 space-y-4">
                          <h3 className="text-cyan-400 font-semibold mb-4">{t('challengeDetails')}</h3>
                          
                          {selectedChallenge ? (
                            <div className="space-y-3">
                              <div className="flex justify-between items-center">
                                <span className="text-gray-300">{t('challengeName')}</span>
                                <span className="text-white">{selectedChallenge.title}</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-gray-300">{t('theme')}</span>
                                <span className="text-white">{selectedChallenge.thematic || t('notSpecified')}</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-gray-300">{t('expirationDate')}</span>
                                <span className="text-white">{selectedChallenge.expirationFormatted}</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-gray-300">{t('daysRemaining')}</span>
                                <span className={`text-white ${selectedChallenge.isExpiringSoon ? 'text-red-400' : 'text-green-400'}`}>
                                  {selectedChallenge.daysLeft} {selectedChallenge.daysLeft !== 1 ? t('days') : t('day')}
                                </span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-gray-300">{t('challengeStatus')}</span>
                                <span className={`${selectedChallenge.isExpiringSoon ? 'text-red-400' : 'text-green-400'}`}>
                                  {selectedChallenge.isExpiringSoon ? t('expiringSoon') : t('inProgress')}
                                </span>
                              </div>
                              {selectedChallenge.description && (
                                <div className="mt-4">
                                  <span className="text-gray-300 block mb-2">{t('challengeDescription')}</span>
                                  <div className="text-white text-sm bg-gray-600 p-3 rounded-lg">
                                    {selectedChallenge.description}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <div className="text-gray-400 text-center py-8">
                                {loadingChallenges ? t('loadingChallengesEllipsis') : t('selectChallengeToSeeDetails')}
                              </div>
                            </div>
                          )}
                          
                          {/* Progress Bar - Keep for now with default values */}
                          {selectedChallenge && (
                            <div className="mt-6">
                              <div className="flex justify-between mb-2">
                                <span className="text-sm text-gray-400">{t('progress')}</span>
                                <span className="text-sm text-gray-400">
                                  {Math.max(0, Math.min(100, Math.round((1 - selectedChallenge.daysLeft / 30) * 100)))}%
                                </span>
                              </div>
                              <div className="w-full bg-gray-600 rounded-full h-3">
                                <div 
                                  className="bg-gradient-to-r from-pink-500 to-cyan-600 h-3 rounded-full" 
                                  style={{width: `${Math.max(0, Math.min(100, Math.round((1 - selectedChallenge.daysLeft / 30) * 100)))}%`}}
                                ></div>
                              </div>
                            </div>
                          )}
                          
                          {/* Action Buttons */}
                          {selectedChallenge && (
                            <div className="flex space-x-3 mt-6">
                              <button 
                                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors border border-gray-500"
                                onClick={handleEndChallengeNow}
                              >
{t('endNow')}
                              </button>
                            </div>
                          )}
                        </div>
                        
                        {/* Rewards Section - 3 columns */}
                        <div className="lg:col-span-3">
                          <h3 className="text-cyan-400 font-semibold mb-4">{t('rewards')}</h3>
                          
                          <div className="space-y-3">
                            {selectedChallenge ? (
                              // Function to safely get and parse rewards
                              (() => {
                                let parsedRewards = null;
                                
                                if (selectedChallenge.rewards) {
                                  try {
                                    // If it's already an object, use it
                                    if (typeof selectedChallenge.rewards === 'object') {
                                      parsedRewards = selectedChallenge.rewards;
                                    } else {
                                      // If it's a string, parse it
                                      parsedRewards = JSON.parse(selectedChallenge.rewards);
                                    }
                                  } catch (error) {
                                    console.error('Error parsing rewards:', error);
                                    parsedRewards = null;
                                  }
                                }
                                
                                return parsedRewards ? (
                                  <>
                                    {/* 1st Place */}
                                    <div className="flex items-center space-x-3 p-3 bg-gray-600 rounded-lg">
                                      <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                                        <span className="text-black font-bold text-sm">1</span>
                                      </div>
                                      <div className="flex-1">
                                        <p className="text-white font-medium">{t('firstPlace')}</p>
                                        {parsedRewards.first && parsedRewards.first.length > 0 ? (
                                          parsedRewards.first.map((reward, index) => (
                                            reward.trim() ? (
                                              <p key={index} className={`text-sm ${index === 0 ? 'text-gray-300' : 'text-gray-400'}`}>
                                                {index === 0 ? '' : '+ '}{reward}
                                              </p>
                                            ) : null
                                          ))
                                        ) : (
                                          <p className="text-gray-400 text-sm">{t('noRewardDefined')}</p>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* 2nd Place */}
                                    <div className="flex items-center space-x-3 p-3 bg-gray-600 rounded-lg">
                                      <div className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center">
                                        <span className="text-black font-bold text-sm">2</span>
                                      </div>
                                      <div className="flex-1">
                                        <p className="text-white font-medium">{t('secondPlace')}</p>
                                        {parsedRewards.second && parsedRewards.second.length > 0 ? (
                                          parsedRewards.second.map((reward, index) => (
                                            reward.trim() ? (
                                              <p key={index} className={`text-sm ${index === 0 ? 'text-gray-300' : 'text-gray-400'}`}>
                                                {index === 0 ? '' : '+ '}{reward}
                                              </p>
                                            ) : null
                                          ))
                                        ) : (
                                          <p className="text-gray-400 text-sm">{t('noRewardDefined')}</p>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* 3rd Place */}
                                    <div className="flex items-center space-x-3 p-3 bg-gray-600 rounded-lg">
                                      <div className="w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center">
                                        <span className="text-black font-bold text-sm">3</span>
                                      </div>
                                      <div className="flex-1">
                                        <p className="text-white font-medium">{t('thirdPlace')}</p>
                                        {parsedRewards.third && parsedRewards.third.length > 0 ? (
                                          parsedRewards.third.map((reward, index) => (
                                            reward.trim() ? (
                                              <p key={index} className={`text-sm ${index === 0 ? 'text-gray-300' : 'text-gray-400'}`}>
                                                {index === 0 ? '' : '+ '}{reward}
                                              </p>
                                            ) : null
                                          ))
                                        ) : (
                                          <p className="text-gray-400 text-sm">{t('noRewardDefined')}</p>
                                        )}
                                      </div>
                                    </div>
                                  </>
                                ) : (
                                  // Fallback when no rewards are defined
                                  <>
                                    {/* 1st Place */}
                                    <div className="flex items-center space-x-3 p-3 bg-gray-600 rounded-lg">
                                      <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                                        <span className="text-black font-bold text-sm">1</span>
                                      </div>
                                      <div className="flex-1">
                                        <p className="text-white font-medium">{t('firstPlace')}</p>
                                        <p className="text-gray-400 text-sm">{t('noChallengeSelected')}</p>
                                      </div>
                                    </div>
                                    
                                    {/* 2nd Place */}
                                    <div className="flex items-center space-x-3 p-3 bg-gray-600 rounded-lg">
                                      <div className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center">
                                        <span className="text-black font-bold text-sm">2</span>
                                      </div>
                                      <div className="flex-1">
                                        <p className="text-white font-medium">{t('secondPlace')}</p>
                                        <p className="text-gray-400 text-sm">{t('noChallengeSelected')}</p>
                                      </div>
                                    </div>
                                    
                                    {/* 3rd Place */}
                                    <div className="flex items-center space-x-3 p-3 bg-gray-600 rounded-lg">
                                      <div className="w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center">
                                        <span className="text-black font-bold text-sm">3</span>
                                      </div>
                                      <div className="flex-1">
                                        <p className="text-white font-medium">{t('thirdPlace')}</p>
                                        <p className="text-gray-400 text-sm">{t('noChallengeSelected')}</p>
                                      </div>
                                    </div>
                                  </>
                                );
                              })()
                            ) : (
                              // Fallback when no challenge is selected
                              <>
                                {/* 1st Place */}
                                <div className="flex items-center space-x-3 p-3 bg-gray-600 rounded-lg">
                                  <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                                    <span className="text-black font-bold text-sm">1</span>
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-white font-medium">{t('firstPlace')}</p>
                                    <p className="text-gray-400 text-sm">{t('noChallengeSelected')}</p>
                                  </div>
                                </div>
                                
                                {/* 2nd Place */}
                                <div className="flex items-center space-x-3 p-3 bg-gray-600 rounded-lg">
                                  <div className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center">
                                    <span className="text-black font-bold text-sm">2</span>
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-white font-medium">{t('secondPlace')}</p>
                                    <p className="text-gray-400 text-sm">{t('noChallengeSelected')}</p>
                                  </div>
                                </div>
                                
                                {/* 3rd Place */}
                                <div className="flex items-center space-x-3 p-3 bg-gray-600 rounded-lg">
                                  <div className="w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center">
                                    <span className="text-black font-bold text-sm">3</span>
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-white font-medium">{t('thirdPlace')}</p>
                                    <p className="text-gray-400 text-sm">{t('noChallengeSelected')}</p>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Create New Challenge Section */}
                    <div id="create-challenge" className="bg-gray-700 rounded-xl p-6" data-section="create-challenge">
                      <h2 className="text-xl font-bold text-yellow-400 mb-6">
                        {isEditMode ? t('editChallenge') : t('createNewChallenge')}
                      </h2>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Basic Info */}
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">{t('challengeNameLabel')}</label>
                            <input
                              type="text"
                              placeholder={t('challengeNamePlaceholder')}
                              className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                              value={challengeForm.name}
                              onChange={(e) => handleChallengeFormChange('name', e.target.value)}
                            />
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-2">{t('startDate')}</label>
                              <input
                                type="date"
                                className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                value={challengeForm.startDate}
                                onChange={(e) => handleChallengeFormChange('startDate', e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-2">{t('endDate')}</label>
                              <input
                                type="date"
                                className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                value={challengeForm.endDate}
                                onChange={(e) => handleChallengeFormChange('endDate', e.target.value)}
                              />
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-2">{t('thematic')}</label>
                              <select className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                value={challengeForm.thematic}
                                onChange={(e) => handleChallengeFormChange('thematic', e.target.value)}
                              >
                                <option>{t('food')}</option>
                                <option>{t('sport')}</option>
                                <option>{t('art')}</option>
                                <option>{t('gaming')}</option>
                                <option>{t('music')}</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-2">{t('challengeColor')}</label>
                              <select 
                                className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                value={challengeForm.color}
                                onChange={(e) => handleChallengeFormChange('color', e.target.value)}
                              >
                                <option value="#06b6d4">🟦 Cyan (#06b6d4)</option>
                                <option value="#10b981">🟩 Emerald (#10b981)</option>
                                <option value="#f59e0b">🟨 Amber (#f59e0b)</option>
                                <option value="#ef4444">🟥 Red (#ef4444)</option>
                                <option value="#8b5cf6">🟪 Violet (#8b5cf6)</option>
                                <option value="#ec4899">🩷 Pink (#ec4899)</option>
                                <option value="#f97316">🟧 Orange (#f97316)</option>
                                <option value="#84cc16">🟢 Lime (#84cc16)</option>
                                <option value="#3b82f6">🔵 Blue (#3b82f6)</option>
                                <option value="#22c55e">💚 Green (#22c55e)</option>
                                <option value="#eab308">💛 Yellow (#eab308)</option>
                                <option value="#a855f7">🔮 Purple (#a855f7)</option>
                              </select>
                              <div className="mt-2 flex items-center space-x-2">
                                <div 
                                  className="w-4 h-4 rounded border border-gray-600"
                                  style={{ backgroundColor: challengeForm.color }}
                                ></div>
                                <span className="text-xs text-gray-400 font-mono">{challengeForm.color}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">{t('challengeDescriptionLabel')}</label>
                            <textarea
                              rows="4"
                              placeholder={t('challengeDescriptionPlaceholder')}
                              className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none"
                              value={challengeForm.description}
                              onChange={(e) => handleChallengeFormChange('description', e.target.value)}
                            ></textarea>
                          </div>
                        </div>
                        
                        {/* Rewards Configuration */}
                        <div>
                          <h3 className="text-cyan-400 font-semibold mb-4">{t('rewards')}</h3>
                          
                          <div className="grid grid-cols-3 gap-4">
                            {/* 1st Place Rewards */}
                            <div className="bg-gray-800 rounded-lg p-4">
                              <div className="flex items-center space-x-2 mb-3">
                                <div className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center">
                                  <span className="text-black font-bold text-xs">1</span>
                                </div>
                                <span className="text-white font-medium text-sm">{t('firstPlace')}</span>
                              </div>
                              
                              <div className="space-y-2">
                                {challengeForm.rewards.first.map((reward, index) => (
                                  <div key={index} className="flex items-center space-x-2">
                                    <input
                                      type="text"
                                      placeholder={`${t('rewardPlaceholder')} ${index + 1}`}
                                      className="flex-1 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-xs placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-pink-500"
                                      value={reward}
                                      onChange={(e) => handleRewardChange('first', index, e.target.value)}
                                    />
                                    {challengeForm.rewards.first.length > 2 && (
                                      <button 
                                        className="text-red-400 hover:text-red-300 text-xs"
                                        onClick={() => removeReward('first', index)}
                                      >
                                        ×
                                      </button>
                                    )}
                                  </div>
                                ))}
                                <button className="text-cyan-400 text-xs hover:text-cyan-300 transition-colors" onClick={() => addReward('first')}>{t('addReward')}</button>
                              </div>
                            </div>
                            
                            {/* 2nd Place Rewards */}
                            <div className="bg-gray-800 rounded-lg p-4">
                              <div className="flex items-center space-x-2 mb-3">
                                <div className="w-6 h-6 bg-gray-400 rounded-full flex items-center justify-center">
                                  <span className="text-black font-bold text-xs">2</span>
                                </div>
                                <span className="text-white font-medium text-sm">{t('secondPlace')}</span>
                              </div>
                              
                              <div className="space-y-2">
                                {challengeForm.rewards.second.map((reward, index) => (
                                  <div key={index} className="flex items-center space-x-2">
                                    <input
                                      type="text"
                                      placeholder={`${t('rewardPlaceholder')} ${index + 1}`}
                                      className="flex-1 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-xs placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-pink-500"
                                      value={reward}
                                      onChange={(e) => handleRewardChange('second', index, e.target.value)}
                                    />
                                    {challengeForm.rewards.second.length > 2 && (
                                      <button 
                                        className="text-red-400 hover:text-red-300 text-xs"
                                        onClick={() => removeReward('second', index)}
                                      >
                                        ×
                                      </button>
                                    )}
                                  </div>
                                ))}
                                <button className="text-cyan-400 text-xs hover:text-cyan-300 transition-colors" onClick={() => addReward('second')}>{t('addReward')}</button>
                              </div>
                            </div>
                            
                            {/* 3rd Place Rewards */}
                            <div className="bg-gray-800 rounded-lg p-4">
                              <div className="flex items-center space-x-2 mb-3">
                                <div className="w-6 h-6 bg-amber-600 rounded-full flex items-center justify-center">
                                  <span className="text-black font-bold text-xs">3</span>
                                </div>
                                <span className="text-white font-medium text-sm">{t('thirdPlace')}</span>
                              </div>
                              
                              <div className="space-y-2">
                                {challengeForm.rewards.third.map((reward, index) => (
                                  <div key={index} className="flex items-center space-x-2">
                                    <input
                                      type="text"
                                      placeholder={`${t('rewardPlaceholder')} ${index + 1}`}
                                      className="flex-1 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-xs placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-pink-500"
                                      value={reward}
                                      onChange={(e) => handleRewardChange('third', index, e.target.value)}
                                    />
                                    {challengeForm.rewards.third.length > 2 && (
                                      <button 
                                        className="text-red-400 hover:text-red-300 text-xs"
                                        onClick={() => removeReward('third', index)}
                                      >
                                        ×
                                      </button>
                                    )}
                                  </div>
                                ))}
                                <button className="text-cyan-400 text-xs hover:text-cyan-300 transition-colors" onClick={() => addReward('third')}>{t('addReward')}</button>
                              </div>
                            </div>
                          </div>
                          
                          {/* Action Buttons - Possitioned in right column as shown in the image */}
                          <div className="flex space-x-4 mt-6">
                            <button 
                              className="bg-pink-600 hover:bg-pink-700 text-white p-[10px] w-[50%] rounded-lg text-lg font-semibold transition-colors shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={handleChallengeSubmit}
                              disabled={isSubmittingChallenge}
                            >
                              {isSubmittingChallenge 
                                ? (isEditMode ? t('editing') : t('creating')) 
                                : (isEditMode ? t('editChallengeButton') : t('createChallengeButton'))
                              }
                            </button>
                            <button 
                              className="bg-gray-600 hover:bg-gray-700 text-white p-[10px] w-[50%] rounded-lg text-lg font-semibold transition-colors shadow-lg hover:shadow-xl transform hover:scale-105"
                              onClick={resetChallengeForm}
                              disabled={isSubmittingChallenge}
                            >
                              {t('cancel')}
                            </button>
                          </div>
                          
                          {/* Error and Success Messages */}
                          {challengeError && (
                            <div className="mt-4 p-3 bg-red-900 border border-red-700 rounded-lg text-red-200">
                              {challengeError}
                            </div>
                          )}
                          {challengeSuccess && (
                            <div className="mt-4 p-3 bg-green-900 border border-green-700 rounded-lg text-green-200">
                              {challengeSuccess}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Interface Streamer Challenge Section */}
                    <div className="bg-gray-700 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-bold text-pink-500">{t('streamerChallengeInterface')}</h2>
                        <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-medium">LIVE</span>
                      </div>
                      
                      {/* Challenge Header with Timer */}
                      <div className="bg-gray-800 rounded-lg p-4 mb-6">
                        <div className="flex items-center justify-between">
                          <h3 className="text-yellow-400 font-semibold text-lg">{t('challengeTitle')}</h3>
                          <div className="flex items-center space-x-2">
                            <span className="text-white font-mono text-lg">12:34</span>
                            <span className="bg-red-600 text-white px-2 py-1 rounded text-xs font-medium">LIVE</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                        {/* Live Submissions */}
                        <div>
                          <h4 className="text-cyan-400 font-semibold mb-3">{t('galleryManagement')}</h4>
                          
                          <div className="space-y-2">
                            <div className="bg-gray-800 rounded-lg p-3 flex items-center justify-between">
                              <div>
                                <span className="text-white font-medium text-sm">{t('designByUser', { number: 1, user: 'user123' })}</span>
                                <div className="flex items-center space-x-1 mt-1">
                                  <span className="text-yellow-400 text-sm">⭐ 4.8</span>
                                </div>
                              </div>
                              <span className="bg-yellow-600 text-black px-2 py-1 rounded text-xs font-medium">TOP 1</span>
                            </div>
                            
                            <div className="bg-gray-800 rounded-lg p-3 flex items-center justify-between">
                              <div>
                                <span className="text-white font-medium text-sm">{t('designByUser', { number: 2, user: 'pasta_lover' })}</span>
                                <div className="flex items-center space-x-1 mt-1">
                                  <span className="text-yellow-400 text-sm">⭐ 4.5</span>
                                </div>
                              </div>
                              <span className="bg-gray-500 text-white px-2 py-1 rounded text-xs font-medium">TOP 2</span>
                            </div>
                            
                            <div className="bg-gray-800 rounded-lg p-3 flex items-center justify-between">
                              <div>
                                <span className="text-white font-medium text-sm">{t('designByUser', { number: 3, user: 'food_fighter' })}</span>
                                <div className="flex items-center space-x-1 mt-1">
                                  <span className="text-yellow-400 text-sm">⭐ 4.2</span>
                                </div>
                              </div>
                              <span className="bg-amber-600 text-black px-2 py-1 rounded text-xs font-medium">TOP 3</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Stream Controls */}
                        <div>
                          <h4 className="text-cyan-400 font-semibold mb-3">{t('streamControls')}</h4>
                          
                          <div className="space-y-3">
                            {/* Small text below title */}
                            <div className="text-gray-400 text-xs">
                              {t('displayMode')}
                            </div>
                            
                            {/* 3 Inline buttons */}
                            <div className="flex space-x-2">
                              <button className="bg-pink-600 text-white px-2 py-1 rounded text-xs font-medium">{t('faceToFace')}</button>
                              <button className="bg-gray-600 hover:bg-gray-700 text-white px-2 py-1 rounded text-xs transition-colors">Top 5</button>
                              <button className="bg-gray-600 hover:bg-gray-700 text-white px-2 py-1 rounded text-xs transition-colors">{t('reset')}</button>
                            </div>
                            
                            {/* Small Timer */}
                            <div>
                              <div className="text-gray-400 text-xs mb-1">{t('timer')}</div>
                              <div className="flex space-x-2">
                                <div className="bg-gray-800 rounded p-2 text-center flex-1">
                                  <div className="text-lg font-bold text-white">5</div>
                                </div>
                                <div className="bg-gray-600 rounded p-2 text-center flex-1">
                                  <div className="text-xs text-white">{t('start')}</div>
                                </div>
                                <div className="bg-gray-600 rounded p-2 text-center flex-1">
                                  <div className="text-xs text-white">{t('reset')}</div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Action Buttons */}
                            <div>
                              <div className="text-gray-400 text-xs mb-2">{t('actions')}</div>
                              <div className="flex space-x-2">
                                <button className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white py-2 rounded text-sm font-medium transition-colors">
                                  {t('startStop')}
                                </button>
                                <button className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded text-sm font-medium transition-colors">
                                  {t('endStream')}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Stream Overlay Preview */}
                      <div>
                        <h4 className="text-cyan-400 font-semibold mb-3">{t('overlayPreview')}</h4>
                        <div className="bg-gray-800 rounded-lg p-4">
                          <div className="text-pink-500 font-bold text-lg text-center mb-2">PIMP MY DROP</div>
                          <div className="text-yellow-400 font-medium text-center mb-4">Challenge Ajoli vs Pasta</div>
                          <div className="grid grid-cols-2 gap-3 mb-4">
                            <div className="bg-gray-700 rounded p-3 text-center">
                              <div className="text-white font-medium">Design #1</div>
                            </div>
                            <div className="bg-gray-700 rounded p-3 text-center">
                              <div className="text-white font-medium">Design #2</div>
                            </div>
                          </div>
                          <div className="text-center space-y-2">
                            <button className="bg-pink-600 hover:bg-pink-700 text-white px-6 py-2 rounded-lg font-medium transition-colors">
                              {t('startStreamButton')}
                            </button>
                            <div>
                              <button className="bg-pink-600 hover:bg-pink-700 text-white px-6 py-2 rounded-lg font-medium transition-colors">
                                {t('startStreamButton')}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Validation des visuels Tab */}
                {activeTab === 'validation' && (
                  <div>
                    {loadingUploads && pendingUploads.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-16 gap-3">
                        <div className="animate-spin rounded-full h-10 w-10 border-2 border-cyan-500 border-t-transparent" aria-hidden />
                        <div className="text-gray-400 text-sm">{t('loadingPendingDesigns')}</div>
                      </div>
                    ) : (
                      <div>
                        {/* Enhanced Filter and Header Section */}
                        <div className="bg-gray-800 rounded-xl p-4 mb-6 border border-gray-700">
                          {/* Top Row - Filters */}
                          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 mb-4">
                            <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                              {/* Display Length Dropdown */}
                              <div className="flex items-center space-x-2">
                                <label className="text-sm font-medium text-gray-300 whitespace-nowrap">{t('showLabel')}</label>
                                <select
                                  value={validationDesignsPerPage}
                                  onChange={(e) => handleDisplayLengthChange(e.target.value)}
                                  className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent cursor-pointer min-w-[80px]"
                                >
                                  <option value="6">6</option>
                                  <option value="12">12</option>
                                  <option value="24">24</option>
                                  <option value="50">50</option>
                                </select>
                                <span className="text-sm text-gray-400 whitespace-nowrap">{t('perPage')}</span>
                              </div>

                              {/* Status Filter Dropdown */}
                              <div className="flex items-center space-x-2">
                                <label className="text-sm font-medium text-gray-300 whitespace-nowrap">{t('statusLabel')}</label>
                                <select
                                  value={statusFilter}
                                  onChange={(e) => handleStatusFilterChange(e.target.value)}
                                  className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent cursor-pointer min-w-[120px]"
                                >
                                  <option value="pending">{t('pendingStatus')}</option>
                                  <option value="aks-pending">{t('pendingAKS')}</option>
                                  <option value="approved">{t('approvedStatus')}</option>
                                  <option value="rejected">{t('rejectedStatus')}</option>
                                  <option value="all">{t('all')}</option>
                                </select>
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                              <button 
                                onClick={handleSelectAll}
                                className="bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-md hover:shadow-lg flex items-center justify-center space-x-2"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                                <span>{selectedUploads.size > 0 ? t('deselectAll') : t('selectAll')}</span>
                              </button>
                              <button 
                                onClick={handleDeleteSelected}
                                disabled={selectedUploads.size === 0}
                                className="bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-md hover:shadow-lg flex items-center justify-center space-x-2"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                </svg>
                                <span>{t('deleteSelected')} ({selectedUploads.size})</span>
                              </button>
                            </div>
                          </div>

                          {/* Bottom Row - Summary */}
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pt-4 border-t border-gray-700 space-y-2 sm:space-y-0">
                            <div className="flex items-center space-x-4">
                              <h4 className="text-lg font-semibold text-white flex items-center gap-2">
                                {pendingUploads.length} {pendingUploads.length > 1 ? t('designs') : t('design')} {statusFilter === 'all' ? t('totalLabel') : statusFilter === 'pending' ? t('pendingStatus') : statusFilter === 'aks-pending' ? t('pendingAKS') : statusFilter === 'approved' ? t('approvedStatus') : t('rejectedStatus')}
                                {loadingUploads && pendingUploads.length > 0 && (
                                  <span className="inline-flex h-4 w-4 animate-spin rounded-full border-2 border-cyan-400 border-t-transparent" title={t('loadingPendingDesigns')} aria-label={t('loadingPendingDesigns')} />
                                )}
                              </h4>
                              {selectedUploads.size > 0 && (
                                <div className="bg-pink-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                                  {selectedUploads.size} {t('selected')}{selectedUploads.size > 1 ? 's' : ''}
                                </div>
                              )}
                            </div>
                            
                            {validationTotalPages > 1 && (
                              <div className="text-sm text-gray-400">
                                Page {validationCurrentPage} {t('of')} {validationTotalPages} • {t('showingResults')} {Math.min(validationStartIndex + 1, pendingUploads.length)} {t('to')} {Math.min(validationEndIndex, pendingUploads.length)} {t('results')}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {pendingUploads.length === 0 ? (
                          <div className="text-center py-8">
                            <div className="text-gray-400 mb-2">
                              {t('noDesign')} {statusFilter === 'all' ? '' : statusFilter === 'pending' ? t('pendingValidation') : statusFilter === 'aks-pending' ? t('pendingAKS') : statusFilter === 'approved' ? t('approvedStatus') : t('rejectedStatus')}
                            </div>
                          </div>
                        ) : (
                          <>
                            {/* 4-Column Grid Layout - Responsive */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
                              {currentValidationUploads.map((upload) => (
                                <div key={upload.id} data-upload-id={upload.id} className="bg-gray-700 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                                  {/* Image */}
                                  <div className="aspect-square bg-gray-600 relative">
                                    {upload.image_url ? (
                                      <img 
                                        src={upload.image_url} 
                                        alt={upload.title}
                                        className={`w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity ${upload.blur ? 'blur-xl' : ''}`}
                                        onClick={() => {
                                          setSelectedImage(upload.image_url);
                                          setSelectedImageBlur(upload.blur || null);
                                          setRevealBlurredImage(false);
                                          setShowImageModal(true);
                                        }}
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                                        <i className="fas fa-image text-2xl"></i>
                                      </div>
                                    )}
                                    
                                    {/* Status Badge and Action Buttons Overlay - Responsive */}
                                    <div className="absolute top-2 lg:top-3 left-2 lg:left-3 right-2 lg:right-3 flex justify-between items-start">
                                      {/* Left Side - Status Badge OR Action Buttons */}
                                      {(upload.status === 0 || upload.status === '0' || statusFilter === 'pending') ? (
                                        /* Action Buttons for Pending Items */
                                        <div className="flex space-x-2">
                                          <button
                                            onClick={() => handleUploadAction(upload.id, 'approved')}
                                            disabled={processingUpload === upload.id}
                                            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-500 text-white px-3 py-2 rounded-lg text-sm font-medium shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 disabled:transform-none"
                                            title={t('approve')}
                                          >
                                            {processingUpload === upload.id && processingAction === 'approve' ? (
                                              <i className="fas fa-spinner fa-spin"></i>
                                            ) : (
                                              <i className="fas fa-check"></i>
                                            )}
                                          </button>
                                          
                                          {/* Reject Button with Dropdown */}
                                          <div className="relative">
                                            <button
                                              onMouseEnter={() => handleRejectHover(upload.id)}
                                              onMouseLeave={handleRejectLeave}
                                              disabled={processingUpload === upload.id}
                                              className="bg-red-600 hover:bg-red-700 disabled:bg-gray-500 text-white px-3 py-2 rounded-lg text-sm font-medium shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 disabled:transform-none"
                                              title={t('reject')}
                                            >
                                              {processingUpload === upload.id && processingAction === 'reject' ? (
                                                <i className="fas fa-spinner fa-spin"></i>
                                              ) : (
                                                <i className="fas fa-times"></i>
                                              )}
                                            </button>
                                            
                                            {/* Rejection Reasons Dropdown */}
                                            {showRejectDropdown === upload.id && (
                                              <div 
                                                className="absolute left-0 top-full mt-2 w-56 lg:w-64 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-50"
                                                onMouseEnter={() => {
                                                  if (rejectHoverTimeout) clearTimeout(rejectHoverTimeout);
                                                }}
                                                onMouseLeave={handleRejectLeave}
                                              >
                                                <div className="p-2">
                                                  <div className="text-xs text-gray-400 px-3 py-2 border-b border-gray-600">
                                                    Raison de rejet:
                                                  </div>
                                                  <button
                                                    onClick={() => handleRejectWithReason(upload.id, 'Invalid image content.')}
                                                    className="w-full text-left px-3 py-2 text-sm text-white hover:bg-gray-700 rounded transition-colors duration-150"
                                                  >
                                                    1. Invalid image content.
                                                  </button>
                                                  <button
                                                    onClick={() => handleRejectWithReason(upload.id, 'Image is not clear.')}
                                                    className="w-full text-left px-3 py-2 text-sm text-white hover:bg-gray-700 rounded transition-colors duration-150"
                                                  >
                                                    2. Image is not clear.
                                                  </button>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      ) : (
                                        /* Status Badge and Publish Button for Non-Pending Items */
                                        <div className="flex items-center space-x-2">
                                          <div className={`px-3 py-2 rounded-lg text-sm font-medium shadow-lg ${
                                            upload.status === 1 || upload.status === '1' ? 'bg-green-600 text-green-100' :
                                            upload.status === 2 || upload.status === '2' ? 'bg-red-600 text-red-100' :
                                            upload.status === 3 || upload.status === '3' ? 'bg-gray-600 text-gray-100' :
                                            'bg-yellow-600 text-yellow-100'
                                          }`}>
                                            {upload.status === 1 || upload.status === '1' ? 'Approved' :
                                             upload.status === 2 || upload.status === '2' ? 'Rejected' :
                                             upload.status === 3 || upload.status === '3' ? 'Deleted' :
                                             'Pending'}
                                          </div>
                                          
                                                                    {/* Publish Button/Status for Approved Items */}
                          {(upload.status === 1 || upload.status === '1') && (
                            <>
                              {(upload.publish === 1 || upload.publish === '1') ? (
                                <div className="flex items-center space-x-2">
                                  <div className="bg-green-600 text-green-100 px-3 py-2 rounded-lg text-sm font-medium shadow-lg">
                                    <i className="fas fa-check mr-1"></i>
                                    {t('published')}
                                  </div>
                                  <button
                                    type="button"
                                    disabled={loadingEditProduct}
                                    onClick={() => handleEditProduct(upload)}
                                    className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white p-2 rounded-lg text-sm font-medium shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                                    title={t('editProduct')}
                                  >
                                    <i className="fas fa-edit" aria-hidden />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => handlePublishUpload(upload)}
                                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm font-medium shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                                    title={t('publish')}
                                >
                                  <i className="fas fa-share-square mr-1"></i>
                                  {t('publish')}
                                </button>
                              )}
                            </>
                          )}
                                        </div>
                                      )}
                                      
                                      {/* Right Side - Checkbox */}
                                      <div className="flex items-center space-x-2">
                                        {/* Checkbox - Always present */}
                                        <label className="flex items-center cursor-pointer">
                                          <input
                                            type="checkbox"
                                            className="sr-only"
                                            checked={selectedUploads.has(upload.id)}
                                            onChange={(e) => handleUploadSelection(upload.id, e.target.checked)}
                                          />
                                          <div className={`w-5 h-5 border-2 rounded-md flex items-center justify-center transition-colors shadow-lg ${
                                            selectedUploads.has(upload.id) 
                                              ? 'bg-pink-600 border-pink-600' 
                                              : 'bg-gray-700 border-gray-500 hover:bg-gray-600'
                                          }`}>
                                            {selectedUploads.has(upload.id) && (
                                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/>
                                              </svg>
                                            )}
                                          </div>
                                        </label>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Content - Responsive padding */}
                                  <div className="p-3 lg:p-5 space-y-2 lg:space-y-3">
                                    {/* Title */}
                                    <div>
                                      <SecureText 
                                        className="upload-title font-semibold text-white text-base lg:text-lg leading-tight block" 
                                        title={upload.title}
                                      >
                                        {upload.title}
                                      </SecureText>
                                    </div>
                                    
                                    {/* Author Info */}
                                    <div className="flex items-center space-x-2">
                                      <div className="w-6 h-6 lg:w-8 lg:h-8 bg-gray-600 rounded-full flex items-center justify-center">
                                        <i className="fas fa-user text-gray-300 text-xs lg:text-sm"></i>
                                      </div>
                                      <div>
                                        <SecureText className="text-xs lg:text-sm text-gray-300 font-medium block">
                                          {upload.author}
                                        </SecureText>
                                        <SecureText className="text-xs text-gray-400 block">
                                          {upload.email}
                                        </SecureText>
                                        <p className="text-xs text-gray-500">
                                          {upload.submittedDate}
                                        </p>
                                      </div>
                                    </div>
                                    
                                    {/* Challenge Info - Only show if challenge_id exists and is not 0 */}
                                   {Boolean(upload.challenge_id) && upload.challenge_id !== 0 && (
  <div className="bg-gray-600 rounded-lg p-2 lg:p-3 space-y-1 lg:space-y-2">
    <div className="flex items-center space-x-2">
      <i className="fas fa-trophy text-yellow-500 text-xs lg:text-sm"></i>
      <SecureText className="text-xs lg:text-sm text-gray-200 font-medium block">
        {upload.challengeTitle}
      </SecureText>
    </div>
    {upload.challengeExpiration && upload.challengeExpiration !== 'N/A' && (
      <div className="flex items-center space-x-2">
        <i className="fas fa-clock text-orange-400 text-xs lg:text-sm"></i>
        <span className="text-xs text-gray-300">
          {t('expiresOn')} {new Date(upload.challengeExpiration).toLocaleDateString('fr-FR')}
        </span>
      </div>
    )}
  </div>
)}
                                    
                                    {/* Available Products */}
                                    <AvailableProducts products={upload.products} compact />

                                    {/* Shopify Data - Only show if published and has shopify_info */}
                                    {(upload.publish === 1 || upload.publish === '1') && upload.shopify_info && (
                                      <div className="bg-blue-600/20 border border-blue-500/30 rounded-lg p-2 lg:p-3 space-y-1">
                                        <div className="flex items-center justify-between mb-2">
                                          <div className="flex items-center space-x-2">
                                            <i className="fab fa-shopify text-green-400 text-xs lg:text-sm"></i>
                                            <span className="text-xs lg:text-sm text-gray-200 font-medium">Shopify Product</span>
                                          </div>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); handleUnpublish(upload); }}
                                            disabled={isUnpublishing && unpublishingUploadId === upload.id}
                                            className="bg-red-600/80 hover:bg-red-600 disabled:bg-red-800/50 disabled:cursor-not-allowed text-white text-xs px-2 py-1 rounded-md transition-colors flex items-center gap-1"
                                            title="Unpublish from Shopify"
                                          >
                                            {isUnpublishing && unpublishingUploadId === upload.id ? (
                                              <><div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div><span>...</span></>
                                            ) : (
                                              <><i className="fas fa-times-circle text-[10px]"></i><span>Unpublish</span></>
                                            )}
                                          </button>
                                        </div>
                                                                                 <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                           <div>
                                             <span className="text-gray-400">ID:</span>
                                             <span className="text-white ml-1 shopify-id">{upload.shopify_info.shopifyId}</span>
                                           </div>
                                           <div>
                                             <span className="text-gray-400">{t('price')}</span>
                                             <span className="text-green-300 ml-1 shopify-price">{upload.shopify_info.shopifyPrice}€</span>
                                           </div>
                                           <div>
                                             <span className="text-gray-400">Stock:</span>
                                             <span className="text-blue-300 ml-1 shopify-stock">{upload.shopify_info.shopifyStock}</span>
                                           </div>
                                           <div>
                                             <span className="text-gray-400">{t('updatedOn')}</span>
                                             <span className="text-gray-300 ml-1">
                                               {upload.shopify_info.updatedAt ? new Date(upload.shopify_info.updatedAt).toLocaleDateString('fr-FR') : 'N/A'}
                                             </span>
                                           </div>
                                         </div>
                                      </div>
                                    )}
                                    
                                    {/* Blur reason if image is blurred */}
                                    {upload.blur && (
                                      <div className="mt-2 p-2 bg-red-900/30 rounded-lg border border-red-700">
                                        <p className="text-red-400 text-xs leading-relaxed">
                                          <i className="fas fa-eye-slash mr-1"></i>
                                          {upload.blur}
                                        </p>
                                      </div>
                                    )}

                                    {/* Rejection reason — same placement/style as blur notice (below Available type / rest of card body) */}
                                    {(upload.status === 2 || upload.status === '2') && upload.reason && String(upload.reason).trim() && (
                                      <div className="mt-2 p-2 bg-red-900/30 rounded-lg border border-red-700">
                                        <p className="text-red-400 text-xs leading-relaxed font-medium">
                                          <i className="fas fa-times-circle mr-1"></i>
                                          {t('reasonForRejection')}
                                        </p>
                                        <SecureText className="text-red-300 text-xs leading-relaxed block mt-1.5">
                                          {upload.reason}
                                        </SecureText>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                            
                            {/* Pagination Controls for Validation */}
                            {validationTotalPages > 1 && (
                              <div className="flex justify-center items-center mt-8 space-x-1 sm:space-x-2">
                                {/* Previous Button */}
                                <button
                                  onClick={goToValidationPreviousPage}
                                  disabled={validationCurrentPage === 1}
                                  className={`px-2 sm:px-4 py-2 rounded-lg font-medium transition-all duration-200 text-sm sm:text-base ${
                                    validationCurrentPage === 1
                                      ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                                      : 'bg-pink-600 text-white hover:bg-pink-700 hover:scale-105'
                                  }`}
                                >
                                  <i className="fa-solid fa-chevron-left mr-1 sm:mr-2"></i>
                                  <span className="hidden sm:inline">{t('previous')}</span>
                                  <span className="sm:hidden">{t('prev')}</span>
                                </button>

                                {/* Page Numbers */}
                                <div className="flex space-x-1">
                                  {[...Array(validationTotalPages)].map((_, index) => {
                                    const page = index + 1;
                                    const isCurrentPage = page === validationCurrentPage;
                                    
                                    // Show all pages if total pages <= 7, otherwise show smart pagination
                                    if (validationTotalPages <= 7) {
                                      return (
                                        <button
                                          key={page}
                                          onClick={() => goToValidationPage(page)}
                                          className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg font-medium transition-all duration-200 text-sm sm:text-base ${
                                            isCurrentPage
                                              ? 'bg-pink-600 text-white shadow-lg scale-110'
                                              : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
                                          }`}
                                        >
                                          {page}
                                        </button>
                                      );
                                    } else {
                                      // Smart pagination for many pages
                                      const showPage = 
                                        page === 1 || 
                                        page === validationTotalPages || 
                                        (page >= validationCurrentPage - 1 && page <= validationCurrentPage + 1);
                                      
                                      if (showPage) {
                                        return (
                                          <button
                                            key={page}
                                            onClick={() => goToValidationPage(page)}
                                            className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg font-medium transition-all duration-200 text-sm sm:text-base ${
                                              isCurrentPage
                                                ? 'bg-pink-600 text-white shadow-lg scale-110'
                                                : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
                                            }`}
                                          >
                                            {page}
                                          </button>
                                        );
                                      } else if (page === validationCurrentPage - 2 || page === validationCurrentPage + 2) {
                                        return (
                                          <span key={page} className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center text-gray-500 text-sm sm:text-base">
                                            ...
                                          </span>
                                        );
                                      }
                                      return null;
                                    }
                                  })}
                                </div>

                                {/* Next Button */}
                                <button
                                  onClick={goToValidationNextPage}
                                  disabled={validationCurrentPage === validationTotalPages}
                                  className={`px-2 sm:px-4 py-2 rounded-lg font-medium transition-all duration-200 text-sm sm:text-base ${
                                    validationCurrentPage === validationTotalPages
                                      ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                                      : 'bg-pink-600 text-white hover:bg-pink-700 hover:scale-105'
                                  }`}
                                >
                                  <span className="hidden sm:inline">{t('next')}</span>
                                  <span className="sm:hidden">{t('nextShort')}</span>
                                  <i className="fa-solid fa-chevron-right ml-1 sm:ml-2"></i>
                                </button>
                              </div>
                            )}

                            {/* Pagination Info */}
                            {pendingUploads.length > 0 && (
                              <div className="text-center mt-6">
                                <p className="text-gray-400 text-sm">
                                  {t('displayingResults')
                                    .replace('{start}', Math.min(validationStartIndex + 1, pendingUploads.length))
                                    .replace('{end}', Math.min(validationEndIndex, pendingUploads.length))
                                    .replace('{total}', pendingUploads.length)
                                    .replace('{plural}', pendingUploads.length > 1 ? 's' : '')
                                  }
                                </p>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Statistiques Tab */}
                {activeTab === 'statistics' && (
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-gray-400">Galerie</span>
                        <span className="text-sm">85%</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div className="bg-pink-500 h-2 rounded-full" style={{width: '85%'}}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-gray-400">Votes</span>
                        <span className="text-sm">72%</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div className="bg-blue-500 h-2 rounded-full" style={{width: '72%'}}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-gray-400">Engagement</span>
                        <span className="text-sm">91%</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{width: '91%'}}></div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Archives Tab */}
                {activeTab === 'archives' && (
                  <div className="space-y-6">
                    {/* Filters Section */}
                    <div className="bg-gray-700 rounded-xl p-4">
                      <div className="flex flex-wrap items-end gap-4">
                        {/* View Toggle */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => { setShowFolderView(false); setSelectedFolder(null); }}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                              !showFolderView ? 'bg-pink-600 text-white' : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                            }`}
                          >
                            <i className="fas fa-list"></i>
                            {t('uploads') || 'Uploads'}
                          </button>
                          <button
                            onClick={() => { setShowFolderView(true); setSelectedFolder(null); fetchArchiveFolders(); }}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                              showFolderView ? 'bg-pink-600 text-white' : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                            }`}
                          >
                            <i className="fas fa-folder"></i>
                            {t('archive') || 'Archive'}
                          </button>
                        </div>

                        {!showFolderView && (
                          <>
                            <div className="flex flex-col">
                              <label className="text-sm text-gray-400 mb-1">{t('from')}</label>
                              <input
                                type="date"
                                value={archiveDateFrom}
                                onChange={(e) => setArchiveDateFrom(e.target.value)}
                                className="bg-gray-600 text-white px-3 py-2 rounded-lg border border-gray-500 focus:border-pink-500 focus:outline-none"
                              />
                            </div>
                            <div className="flex flex-col">
                              <label className="text-sm text-gray-400 mb-1">{t('to')}</label>
                              <input
                                type="date"
                                value={archiveDateTo}
                                onChange={(e) => setArchiveDateTo(e.target.value)}
                                className="bg-gray-600 text-white px-3 py-2 rounded-lg border border-gray-500 focus:border-pink-500 focus:outline-none"
                              />
                            </div>
                            <div className="flex flex-col">
                              <label className="text-sm text-gray-400 mb-1">{t('status')}</label>
                              <select
                                value={archiveStatusFilter}
                                onChange={(e) => setArchiveStatusFilter(e.target.value)}
                                className="bg-gray-600 text-white px-3 py-2 rounded-lg border border-gray-500 focus:border-pink-500 focus:outline-none min-w-[150px]"
                              >
                                <option value="1">{t('approved')}</option>
                                <option value="4">{t('archived')}</option>
                              </select>
                            </div>

                            <div className="flex items-center gap-2 ml-auto">
                              <label className="flex items-center gap-2 text-gray-300 cursor-pointer mr-4">
                                <input
                                  type="checkbox"
                                  checked={archiveUploads.length > 0 && selectedArchiveUploads.size === archiveUploads.length}
                                  onChange={toggleSelectAllArchives}
                                  className="w-5 h-5 rounded bg-gray-600 border-gray-500 text-pink-500 focus:ring-pink-500 focus:ring-offset-gray-800"
                                />
                                <span className="text-sm">{t('selectAll')}</span>
                              </label>
                              <button
                                onClick={openArchiveModal}
                                disabled={processingArchiveAction || selectedArchiveUploads.size === 0}
                                className="bg-orange-600 hover:bg-orange-700 disabled:bg-gray-500 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
                              >
                                {processingArchiveAction ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-archive"></i>}
                                {t('archive')}
                              </button>
                              <button
                                onClick={handleRestoreAction}
                                disabled={processingArchiveAction || selectedArchiveUploads.size === 0}
                                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-500 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
                              >
                                {processingArchiveAction ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-undo"></i>}
                                {t('restore')}
                              </button>
                            </div>
                          </>
                        )}

                        {showFolderView && selectedFolder && (
                          <div className="flex items-center gap-2 ml-auto">
                            <label className="flex items-center gap-2 text-gray-300 cursor-pointer mr-4">
                              <input
                                type="checkbox"
                                checked={folderUploads.length > 0 && selectedArchiveUploads.size === folderUploads.length}
                                onChange={() => {
                                  if (selectedArchiveUploads.size === folderUploads.length) {
                                    setSelectedArchiveUploads(new Set());
                                  } else {
                                    setSelectedArchiveUploads(new Set(folderUploads.map(u => u.id)));
                                  }
                                }}
                                className="w-5 h-5 rounded bg-gray-600 border-gray-500 text-pink-500 focus:ring-pink-500 focus:ring-offset-gray-800"
                              />
                              <span className="text-sm">{t('selectAll')}</span>
                            </label>
                            <button
                              onClick={handleRestoreAction}
                              disabled={processingArchiveAction || selectedArchiveUploads.size === 0}
                              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-500 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
                            >
                              {processingArchiveAction ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-undo"></i>}
                              {t('restore')}
                            </button>
                          </div>
                        )}
                      </div>

                      {!showFolderView && selectedArchiveUploads.size > 0 && (
                        <div className="mt-3 text-sm text-pink-400">
                          {selectedArchiveUploads.size} {t('uploadsSelected')}
                        </div>
                      )}
                      {showFolderView && selectedFolder && selectedArchiveUploads.size > 0 && (
                        <div className="mt-3 text-sm text-pink-400">
                          {selectedArchiveUploads.size} {t('uploadsSelected')}
                        </div>
                      )}
                    </div>

                    {/* Folder View */}
                    {showFolderView ? (
                      selectedFolder ? (
                        <>
                          {/* Folder breadcrumb */}
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => { setSelectedFolder(null); setSelectedArchiveUploads(new Set()); }}
                              className="flex items-center gap-2 text-pink-400 hover:text-pink-300 transition-colors"
                            >
                              <i className="fas fa-arrow-left"></i>
                              <span className="font-medium">{t('back') || 'Back'}</span>
                            </button>
                            <span className="text-gray-500">|</span>
                            <div className="flex items-center gap-2">
                              <i className="fas fa-folder-open text-amber-400"></i>
                              <span className="text-white font-semibold text-lg">{selectedFolder.name}</span>
                            </div>
                            {selectedFolder.description && (
                              <span className="text-gray-400 text-sm ml-2">— {selectedFolder.description}</span>
                            )}
                            <button
                              onClick={() => handleDeleteFolder(selectedFolder)}
                              className="ml-auto flex items-center gap-2 px-3 py-1.5 bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white rounded-lg transition-all text-sm font-medium"
                            >
                              <i className="fas fa-trash text-xs"></i>
                              {t('deleteFolder') || 'Delete Folder'}
                            </button>
                          </div>

                          {/* Folder uploads grid */}
                          {loadingFolderUploads ? (
                            <div className="flex items-center justify-center py-8">
                              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-500"></div>
                            </div>
                          ) : folderUploads.length === 0 ? (
                            <div className="text-center py-12">
                              <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                                <i className="fas fa-folder-open text-2xl text-gray-400"></i>
                              </div>
                              <p className="text-gray-400">{t('emptyFolder') || 'This folder is empty'}</p>
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                              {folderUploads.map((upload) => (
                                <div
                                  key={upload.id}
                                  className={`relative bg-gray-700 rounded-xl overflow-hidden border-2 transition-all duration-200 ${
                                    selectedArchiveUploads.has(upload.id)
                                      ? 'border-pink-500 shadow-lg shadow-pink-500/20'
                                      : 'border-transparent hover:border-gray-500'
                                  }`}
                                >
                                  <div className="absolute top-3 left-3 z-10">
                                    <input
                                      type="checkbox"
                                      checked={selectedArchiveUploads.has(upload.id)}
                                      onChange={() => toggleArchiveSelection(upload.id)}
                                      className="w-5 h-5 rounded bg-gray-600 border-gray-500 text-pink-500 focus:ring-pink-500 focus:ring-offset-gray-800 cursor-pointer"
                                    />
                                  </div>
                                  <div className="absolute top-3 right-3 z-10">
                                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-600 text-white">
                                      {t('archived')}
                                    </span>
                                  </div>
                                  <div className="aspect-square cursor-pointer" onClick={() => toggleArchiveSelection(upload.id)}>
                                    <img src={upload.image_url} alt={upload.title || 'Upload'} className="w-full h-full object-cover" loading="lazy" />
                                  </div>
                                  <div className="p-3 space-y-2">
                                    <p className="text-white font-medium truncate">{upload.title || 'Untitled'}</p>
                                    <p className="text-sm text-gray-400 truncate">{upload.email || 'Unknown'}</p>
                                    <p className="text-xs text-gray-500">
                                      {upload.submitted_at ? new Date(upload.submitted_at).toLocaleDateString() : 'N/A'}
                                    </p>
                                    <AvailableProducts products={upload.products} compact />
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        /* Folders Grid */
                        loadingFolders ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-500"></div>
                          </div>
                        ) : archiveFolders.length === 0 ? (
                          <div className="text-center py-12">
                            <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                              <i className="fas fa-folder text-2xl text-gray-400"></i>
                            </div>
                            <p className="text-gray-400">{t('noArchiveFolders') || 'No archive folders yet'}</p>
                            <p className="text-gray-500 text-sm mt-1">{t('archiveFolderHint') || 'Archive designs from the list view to create folders'}</p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {archiveFolders.map((folder) => (
                              <div
                                key={folder.id}
                                className="relative bg-gray-700 rounded-xl p-5 border-2 border-transparent hover:border-pink-500/50 cursor-pointer transition-all duration-200 hover:shadow-lg hover:shadow-pink-500/10 group"
                              >
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder); }}
                                  className="absolute top-3 right-3 w-8 h-8 bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all z-10"
                                  title={t('deleteFolder') || 'Delete folder'}
                                >
                                  <i className="fas fa-trash text-xs"></i>
                                </button>
                                <div
                                  onClick={() => {
                                    setSelectedFolder(folder);
                                    setSelectedArchiveUploads(new Set());
                                    fetchFolderUploads(folder.id);
                                  }}
                                  className="flex items-start gap-4"
                                >
                                  <div className="w-14 h-14 bg-amber-600/20 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-amber-600/30 transition-colors">
                                    <i className="fas fa-folder text-2xl text-amber-400"></i>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h4 className="text-white font-semibold truncate group-hover:text-pink-300 transition-colors">{folder.name}</h4>
                                    {folder.description && (
                                      <p className="text-gray-400 text-sm mt-1 line-clamp-2">{folder.description}</p>
                                    )}
                                    <div className="flex items-center gap-3 mt-3">
                                      <span className="text-xs text-gray-500 flex items-center gap-1">
                                        <i className="fas fa-images"></i>
                                        {folder.upload_count} {folder.upload_count === 1 ? 'design' : 'designs'}
                                      </span>
                                      <span className="text-xs text-gray-500">
                                        {new Date(folder.created_at).toLocaleDateString()}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )
                      )
                    ) : (
                      /* List View (original uploads grid) */
                      loadingArchives ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-500"></div>
                        </div>
                      ) : archiveUploads.length === 0 ? (
                        <div className="text-center py-12">
                          <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="fas fa-archive text-2xl text-gray-400"></i>
                          </div>
                          <p className="text-gray-400">{t('noUploadsFound')}</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                          {archiveUploads.map((upload) => (
                            <div
                              key={upload.id}
                              className={`relative bg-gray-700 rounded-xl overflow-hidden border-2 transition-all duration-200 ${
                                selectedArchiveUploads.has(upload.id)
                                  ? 'border-pink-500 shadow-lg shadow-pink-500/20'
                                  : 'border-transparent hover:border-gray-500'
                              }`}
                            >
                              <div className="absolute top-3 left-3 z-10">
                                <input
                                  type="checkbox"
                                  checked={selectedArchiveUploads.has(upload.id)}
                                  onChange={() => toggleArchiveSelection(upload.id)}
                                  className="w-5 h-5 rounded bg-gray-600 border-gray-500 text-pink-500 focus:ring-pink-500 focus:ring-offset-gray-800 cursor-pointer"
                                />
                              </div>
                              <div className="absolute top-3 right-3 z-10">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  upload.status === 1 || upload.status === '1'
                                    ? 'bg-green-600 text-white'
                                    : 'bg-orange-600 text-white'
                                }`}>
                                  {upload.status === 1 || upload.status === '1' ? t('approved') : t('archived')}
                                </span>
                              </div>
                              <div className="aspect-square cursor-pointer" onClick={() => toggleArchiveSelection(upload.id)}>
                                <img src={upload.image_url} alt={upload.title || 'Upload'} className="w-full h-full object-cover" loading="lazy" />
                              </div>
                              <div className="p-3 space-y-2">
                                <p className="text-white font-medium truncate">{upload.title || 'Untitled'}</p>
                                <p className="text-sm text-gray-400 truncate">{upload.email || 'Unknown'}</p>
                                <p className="text-xs text-gray-500">
                                  {upload.submitted_at ? new Date(upload.submitted_at).toLocaleDateString() : 'N/A'}
                                </p>
                                <AvailableProducts products={upload.products} compact />
                              </div>
                            </div>
                          ))}
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Delete Folder Confirmation Modal */}
      {deletingFolder && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1100] flex items-center justify-center p-4" onClick={() => setDeletingFolder(null)}>
          <div className="bg-gray-800 rounded-2xl w-full max-w-sm shadow-2xl border border-gray-700" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 text-center">
              <div className="w-14 h-14 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-trash text-2xl text-red-400"></i>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{t('deleteFolder') || 'Delete Folder'}</h3>
              <p className="text-gray-400 text-sm mb-1">
                {t('deleteFolderConfirm') || 'Are you sure you want to delete'}
                <span className="text-white font-medium"> "{deletingFolder.name}"</span>?
              </p>
              <p className="text-gray-500 text-xs">
                {deletingFolder.upload_count || 0} design(s) will be restored to approved status.
              </p>
            </div>
            <div className="p-4 border-t border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setDeletingFolder(null)}
                className="px-4 py-2 rounded-lg bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors font-medium"
              >
                {t('cancel')}
              </button>
              <button
                onClick={confirmDeleteFolder}
                className="px-5 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors font-medium flex items-center gap-2"
              >
                <i className="fas fa-trash text-xs"></i>
                {t('delete') || 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Modal */}
      {showArchiveModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1100] flex items-center justify-center p-4" onClick={() => setShowArchiveModal(false)}>
          <div className="bg-gray-800 rounded-2xl w-full max-w-md shadow-2xl border border-gray-700" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-600/20 rounded-xl flex items-center justify-center">
                  <i className="fas fa-archive text-orange-400"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">{t('archiveDesigns') || 'Archive Designs'}</h3>
                  <p className="text-sm text-gray-400">{selectedArchiveUploads.size} {selectedArchiveUploads.size === 1 ? 'design' : 'designs'} selected</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Choose: new folder or existing */}
              <div className="flex gap-2">
                <button
                  onClick={() => setArchiveModalAction('new')}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    archiveModalAction === 'new' ? 'bg-pink-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  <i className="fas fa-plus mr-2"></i>
                  {t('newFolder') || 'New Folder'}
                </button>
                <button
                  onClick={() => setArchiveModalAction(existingArchives.length > 0 ? existingArchives[0].id : 'new')}
                  disabled={existingArchives.length === 0}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    archiveModalAction !== 'new' ? 'bg-pink-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <i className="fas fa-folder mr-2"></i>
                  {t('existingFolder') || 'Existing Folder'}
                </button>
              </div>

              {archiveModalAction === 'new' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1.5">{t('archiveName') || 'Archive Name'} *</label>
                    <input
                      type="text"
                      value={archiveModalName}
                      onChange={(e) => setArchiveModalName(e.target.value)}
                      placeholder={t('archiveNamePlaceholder') || 'e.g., Summer Collection 2026'}
                      className="w-full bg-gray-700 text-white px-4 py-2.5 rounded-lg border border-gray-600 focus:border-pink-500 focus:outline-none focus:ring-2 focus:ring-pink-500/20 placeholder-gray-500"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1.5">{t('description') || 'Description'}</label>
                    <textarea
                      value={archiveModalDescription}
                      onChange={(e) => setArchiveModalDescription(e.target.value)}
                      placeholder={t('archiveDescPlaceholder') || 'Optional description for this archive...'}
                      rows={3}
                      className="w-full bg-gray-700 text-white px-4 py-2.5 rounded-lg border border-gray-600 focus:border-pink-500 focus:outline-none focus:ring-2 focus:ring-pink-500/20 placeholder-gray-500 resize-none"
                    />
                  </div>
                </>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">{t('selectFolder') || 'Select Folder'}</label>
                  <select
                    value={archiveModalAction}
                    onChange={(e) => setArchiveModalAction(e.target.value)}
                    className="w-full bg-gray-700 text-white px-4 py-2.5 rounded-lg border border-gray-600 focus:border-pink-500 focus:outline-none"
                  >
                    {existingArchives.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.name} ({a.upload_count} designs)
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setShowArchiveModal(false)}
                className="px-4 py-2 rounded-lg bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors font-medium"
              >
                {t('cancel')}
              </button>
              <button
                onClick={submitArchiveAction}
                disabled={archiveModalAction === 'new' && !archiveModalName.trim()}
                className="px-5 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 disabled:bg-gray-500 disabled:cursor-not-allowed text-white transition-colors font-medium flex items-center gap-2"
              >
                <i className="fas fa-archive"></i>
                {t('archive')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {showToast && (
        <div className={`fixed top-4 right-4 z-[100] transform transition-all duration-500 ease-in-out ${
          showToast ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
        }`}>
          <div className={`flex items-center p-4 rounded-lg shadow-lg min-w-[300px] max-w-md ${
            toastType === 'success' 
              ? 'bg-green-600 text-white' 
              : 'bg-red-600 text-white'
          }`}>
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                {toastType === 'success' ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                <p className="font-medium">{toastMessage}</p>
              </div>
              <button 
                onClick={() => setShowToast(false)}
                className="flex-shrink-0 text-white hover:text-gray-200 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1100]">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4 border border-gray-600">
            {/* Modal Header */}
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Supprimer le challenge</h3>
                <p className="text-gray-400 text-sm">Cette action ne peut pas être annulée</p>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="mb-6">
              <p className="text-gray-300 mb-3">
                Êtes-vous sûr de vouloir supprimer le challenge :
              </p>
              <div className="bg-gray-700 rounded-lg p-3 border border-gray-600">
                <p className="text-white font-medium">
                  "{challengeToDelete?.title}"
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  Thématique: {challengeToDelete?.thematic}
                </p>
                <p className="text-gray-400 text-sm">
                  Expire le: {challengeToDelete?.expirationFormatted}
                </p>
              </div>
            </div>
            
            {/* Modal Actions */}
            <div className="flex space-x-3">
              <button
                onClick={cancelDeleteChallenge}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-3 px-4 rounded-lg font-medium transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={confirmDeleteChallenge}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 px-4 rounded-lg font-medium transition-colors"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unpublish Confirmation Modal */}
      {showUnpublishModal && uploadToUnpublish && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1100]">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4 border border-gray-600">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
                <i className="fab fa-shopify text-white text-xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Unpublish from Shopify</h3>
                <p className="text-gray-400 text-sm">This will remove the product from your store</p>
              </div>
            </div>
            <div className="mb-6">
              <p className="text-gray-300 mb-3">
                Are you sure you want to unpublish this product?
              </p>
              <div className="bg-gray-700 rounded-lg p-3 border border-gray-600">
                <p className="text-white font-medium">
                  "{uploadToUnpublish.title || uploadToUnpublish.name || 'Untitled'}"
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  Shopify ID: {uploadToUnpublish.shopify_info?.shopifyId}
                </p>
                <p className="text-gray-400 text-sm">
                  Price: {uploadToUnpublish.shopify_info?.shopifyPrice}€
                </p>
              </div>
              <p className="text-red-400 text-xs mt-3">
                <i className="fas fa-exclamation-triangle mr-1"></i>
                This action will delete the product from Shopify and cannot be undone.
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={cancelUnpublish}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-3 px-4 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmUnpublish}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 px-4 rounded-lg font-medium transition-colors"
              >
                <i className="fas fa-times-circle mr-2"></i>
                Unpublish
              </button>
            </div>
          </div>
        </div>
      )}

      {/* End Challenge Confirmation Modal */}
      {showEndChallengeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1100]">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4 border border-gray-600">
            {/* Modal Header */}
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Terminer le challenge maintenant</h3>
                <p className="text-gray-400 text-sm">Cette action ne peut pas être annulée</p>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="mb-6">
              <p className="text-gray-300 mb-2">
                Êtes-vous sûr de vouloir terminer le challenge maintenant ?
              </p>
              <p className="text-yellow-400 text-sm mb-3">
                ⚠️ Le challenge sera immédiatement terminé
              </p>
              <div className="bg-gray-700 rounded-lg p-3 border border-gray-600">
                <p className="text-white font-medium">
                  "{challengeToEnd?.title}"
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  Thématique: {challengeToEnd?.thematic}
                </p>
                <p className="text-gray-400 text-sm">
                  Expire actuellement le: {challengeToEnd?.expirationFormatted}
                </p>
              </div>
            </div>
            
            {/* Modal Actions */}
            <div className="flex space-x-3">
              <button
                onClick={cancelEndChallenge}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-3 px-4 rounded-lg font-medium transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={confirmEndChallenge}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg font-medium transition-colors"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Designs Confirmation Modal */}
      {showDeleteDesignsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1100]">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4 border border-gray-600">
            {/* Modal Header */}
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Supprimer les designs sélectionnés</h3>
                <p className="text-gray-400 text-sm">Cette action ne peut pas être annulée</p>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="mb-6">
              <p className="text-gray-300 mb-3">
                Êtes-vous sûr de vouloir supprimer les designs sélectionnés ?
              </p>
              <div className="bg-gray-700 rounded-lg p-3 border border-gray-600">
                <p className="text-white font-medium">
                  {designsToDelete.length} design{designsToDelete.length > 1 ? 's' : ''} sélectionné{designsToDelete.length > 1 ? 's' : ''}
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  Cette action supprimera définitivement les designs de la base de données
                </p>
              </div>
            </div>
            
            {/* Modal Actions */}
            <div className="flex space-x-3">
              <button
                onClick={cancelDeleteDesigns}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-3 px-4 rounded-lg font-medium transition-colors"
              >
                {t('cancel')}
              </button>
              <button
                onClick={confirmDeleteDesigns}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 px-4 rounded-lg font-medium transition-colors"
              >
                {t('delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Publish to Shopify Modal */}
      {showPublishModal && selectedUploadForPublish && (
        <PublishModal
          isOpen={showPublishModal}
          onClose={handlePublishModalClose}
          upload={selectedUploadForPublish}
          onPublish={handleShopifyPublish}
          isPublishing={isPublishing}
        />
      )}

      {/* Edit Product Modal */}
      {showEditProductModal && editingProduct && (
        <EditProductModal
          isOpen={showEditProductModal}
          onClose={handleEditProductModalClose}
          product={editingProduct}
          onProductUpdated={handleProductUpdated}
          enableVendorEdit={true}
        />
      )}

      {/* Appeal Modal */}
      {showAppealModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1100] p-4">
          <div className="bg-gray-800 rounded-xl max-w-lg w-full p-6">
            <h3 className="text-2xl font-bold text-white mb-4">Send Appeal</h3>
            <p className="text-gray-300 mb-4">
              Explain why your account should be unblocked. Your appeal will be reviewed by the administration team.
            </p>
            <textarea
              value={appealMessage}
              onChange={(e) => setAppealMessage(e.target.value)}
              placeholder="Enter your appeal message..."
              className="w-full bg-gray-700 text-white rounded-lg p-3 mb-4 h-40 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowAppealModal(false);
                  setAppealMessage('');
                }}
                disabled={isSendingAppeal}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendAppeal}
                disabled={isSendingAppeal || !appealMessage.trim()}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {isSendingAppeal ? 'Sending...' : 'Send Appeal'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Popup Modal */}
      <ImageModal 
        isOpen={showImageModal} 
        imageUrl={selectedImage}
        blurReason={selectedImageBlur}
        revealBlurred={revealBlurredImage}
        onReveal={() => setRevealBlurredImage(true)}
        onClose={() => {
          setShowImageModal(false);
          setSelectedImage(null);
          setSelectedImageBlur(null);
          setRevealBlurredImage(false);
        }}
        t={t}
      />
    </div>
  );
};

// PublishModal Component
const PUBLISH_PRODUCT_LABELS = { tshirt: '👕 T-Shirt', mug: '☕ Mug', poster: '🖼️ Poster', totebag: '👜 Tote Bag', casquette: '🧢 Cap' };
/** Pricing & inventory defaults when opening Add/Publish product modal */
const PUBLISH_DEFAULT_PRICE = '10.00';
const PUBLISH_DEFAULT_STOCK = '5';

const PublishModal = ({ isOpen, onClose, upload, onPublish, isPublishing }) => {
  const { admin } = useAdmin();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    comparePrice: '',
    costPerItem: '',
    category: '',
    vendor: '',
    tags: '',
    weight: '0.0',
    weightUnit: 'kg',
    status: 'active',
    trackQuantity: true,
    continueSellingOutOfStock: false,
    hasSKU: false,
    sku: '',
    barcode: '',
    isPhysicalProduct: true,
    chargeTax: true,
    images: []
  });

  const [typePrices, setTypePrices] = useState({});
  /** Per product type and quality tier — same shape as EditProductModal / Admin Store. */
  const [typeStock, setTypeStock] = useState({});

  // Add state for validation errors
  const [validationErrors, setValidationErrors] = useState({
    price: '',
    stock: ''
  });

  // Pre-populate form data when upload changes
  useEffect(() => {
    if (upload) {
      setFormData({
        title: upload.title || '',
        description: `Design created by ${upload.author}`,
        comparePrice: '',
        costPerItem: '',
        category: upload.productsList && upload.productsList.length > 0 ? upload.productsList.join(', ') : '',
        vendor: admin?.owner || '',
        tags: upload.productsList && upload.productsList.length > 0 ? upload.productsList.join(', ') : '',
        weight: '0.0',
        weightUnit: 'kg',
        status: 'active',
        trackQuantity: true,
        continueSellingOutOfStock: false,
        hasSKU: false,
        sku: '',
        barcode: '',
        isPhysicalProduct: true,
        chargeTax: true,
        images: upload.image_url ? [{ url: upload.image_url, alt: upload.title }] : []
      });

      const products = upload?.products;
      const isAll = !products || typeof products !== 'object' || Object.keys(products).length === 0 || Object.values(products).every(v => v);
      const availableKeys = isAll ? Object.keys(PUBLISH_PRODUCT_LABELS) : Object.keys(PUBLISH_PRODUCT_LABELS).filter(k => products[k] === true);
      const prices = {};
      const stock = {};
      availableKeys.forEach(k => {
        prices[k] =
          k === 'tshirt'
            ? { standard: PUBLISH_DEFAULT_PRICE, premium: PUBLISH_DEFAULT_PRICE, deluxe: PUBLISH_DEFAULT_PRICE }
            : PUBLISH_DEFAULT_PRICE;
        stock[k] = {
          standard: PUBLISH_DEFAULT_STOCK,
          premium: PUBLISH_DEFAULT_STOCK,
          deluxe: PUBLISH_DEFAULT_STOCK,
        };
      });
      setTypePrices(prices);
      setTypeStock(stock);
    }
  }, [upload, admin?.owner]);

  const totalAvailableByVariant = useMemo(() => {
    return Object.values(typeStock).reduce((sum, row) => {
      if (!row) return sum;
      return (
        sum +
        (parseInt(row.standard, 10) || 0) +
        (parseInt(row.premium, 10) || 0) +
        (parseInt(row.deluxe, 10) || 0)
      );
    }, 0);
  }, [typeStock]);

  const handleInputChange = (e) => {
    const { name, type, checked } = e.target;
    const value = type === 'checkbox' ? checked : e.target.value;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'price') {
      setValidationErrors(prev => ({ ...prev, price: '' }));
    }
    if (name === 'trackQuantity') {
      setValidationErrors(prev => ({ ...prev, stock: '' }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const errors = {};
    
    const allPricesValid = Object.entries(typePrices).every(([k, v]) => {
      if (k === 'tshirt' && typeof v === 'object') {
        return parseFloat(v.standard) > 0 && parseFloat(v.premium) > 0 && parseFloat(v.deluxe) > 0;
      }
      return parseFloat(v) > 0;
    });
    if (!allPricesValid) {
      errors.price = 'All product type prices must be greater than 0';
    }
    
    if (formData.trackQuantity) {
      const entries = Object.entries(typePrices);
      const hasTs = entries.some(([k]) => k === 'tshirt');
      let stockInvalid = false;
      entries.forEach(([typeKey]) => {
        const isTshirtRow = typeKey === 'tshirt';
        const tiers = isTshirtRow && hasTs ? ['standard', 'premium', 'deluxe'] : ['standard'];
        tiers.forEach((tier) => {
          const raw = typeStock[typeKey]?.[tier] ?? '0';
          const n = parseInt(String(raw).replace(/\D/g, '') || '0', 10);
          if (n <= 0) stockInvalid = true;
        });
      });
      if (stockInvalid) {
        errors.stock = 'Each variant needs a quantity greater than 0 when tracking inventory';
      }
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    setValidationErrors({ price: '', stock: '' });

    const typeEntries = Object.entries(typePrices);
    const hasTshirt = typeEntries.some(([k]) => k === 'tshirt');

    const variants = [];
    typeEntries.forEach(([typeKey, val]) => {
      const label = PUBLISH_PRODUCT_LABELS[typeKey] || typeKey;
      const isTshirt = typeKey === 'tshirt';
      const qualities = (isTshirt && hasTshirt) ? ['Standard', 'Premium', 'Deluxe'] : ['Standard'];
      qualities.forEach(quality => {
        const slot = quality.toLowerCase();
        const invRaw = typeStock[typeKey]?.[slot] ?? '0';
        const inventory_quantity = Math.max(
          0,
          parseInt(String(invRaw).replace(/\D/g, '') || '0', 10) || 0
        );
        const price = isTshirt && typeof val === 'object'
          ? (parseFloat(val[slot]) || 0).toFixed(2)
          : (parseFloat(val) || 0).toFixed(2);
        const variant = {
          option1: label,
          price,
          compare_at_price: formData.comparePrice || null,
          inventory_quantity: formData.trackQuantity ? inventory_quantity : 0,
          inventory_management: formData.trackQuantity ? 'shopify' : null,
          inventory_policy: formData.continueSellingOutOfStock ? 'continue' : 'deny',
          weight: parseFloat(formData.weight) || 0,
          weight_unit: formData.weightUnit || 'kg',
          sku: formData.hasSKU && formData.sku
            ? (hasTshirt ? `${formData.sku}-${typeKey}-${quality.toLowerCase()}` : `${formData.sku}-${typeKey}`)
            : null,
          barcode: formData.barcode || null,
          taxable: formData.chargeTax,
          requires_shipping: formData.isPhysicalProduct
        };
        if (hasTshirt) variant.option2 = quality;
        variants.push(variant);
      });
    });

    const options = hasTshirt
      ? [
          { name: 'Type', values: typeEntries.map(([k]) => PUBLISH_PRODUCT_LABELS[k] || k) },
          { name: 'Quality', values: ['Standard', 'Premium', 'Deluxe'] }
        ]
      : [{ name: 'Type', values: typeEntries.map(([k]) => PUBLISH_PRODUCT_LABELS[k] || k) }];

    const productData = {
      title: formData.title,
      body_html: formData.description,
      product_type: formData.category,
      vendor: formData.vendor,
      tags: formData.tags,
      status: (formData.status || 'active').toLowerCase(),
      options,
      variants,
      images: formData.images.map(img => ({
        src: img.url,
        alt: img.alt
      }))
    };

    onPublish(productData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
      <div className="bg-gray-800 rounded-2xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden border border-gray-700 flex flex-col">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-pink-600 to-purple-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <i className="fas fa-box text-white"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Add New Product</h2>
                <p className="text-pink-100 text-sm">Create a new product for your store</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/10 rounded-lg"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Modal Content - Scrollable */}
        <div className="flex-1 overflow-y-auto sidebar-scroll p-6">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Column - Main Product Info */}
              <div className="lg:col-span-2 space-y-8">
                {/* Basic Information Card */}
                <div className="bg-gray-700 border border-gray-600 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-pink-600/20 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-white">Basic Information</h3>
                  </div>
                  
                  <div className="space-y-6">
                    {/* Title */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-500 mb-3">
                        Product Title
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-600 rounded-xl focus:outline-none focus:ring-3 focus:ring-pink-500/20 focus:border-pink-500 text-white bg-gray-800 transition-all duration-200 text-lg font-medium"
                        placeholder="e.g., Premium Cotton T-Shirt"
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-3">
                        Product Description
                      </label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows={5}
                        className="w-full px-4 py-3 border border-gray-600 rounded-xl focus:outline-none focus:ring-3 focus:ring-pink-500/20 focus:border-pink-500 text-white bg-gray-800 transition-all duration-200 resize-none"
                        placeholder="Describe your product features, materials, and benefits..."
                      />
                    </div>
                  </div>
                </div>

                {/* Media Upload Card */}
                <div className="bg-gray-700 border border-gray-600 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-purple-600/20 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white">Product Media</h3>
                      <p className="text-sm text-gray-400">Pre-loaded from design submission</p>
                    </div>
                  </div>
                  
                  {formData.images.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                      {formData.images.map((image, index) => (
                        <div key={index} className="relative group">
                          <div className="aspect-square rounded-xl overflow-hidden border-2 border-gray-600">
                            <img
                              src={image.url}
                              alt={image.alt}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                            />
                          </div>
                          <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm shadow-lg">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Pricing & inventory — per variant (same model as Admin Store / Edit product modal) */}
                <div className="bg-gradient-to-br from-green-900/50 to-emerald-900/50 border border-green-700/50 rounded-2xl p-6 shadow-sm">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-green-600/20 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-white">Pricing & inventory</h3>
                        <p className="text-sm text-gray-400">Set price (€) and stock for each variant</p>
                      </div>
                    </div>
                    {formData.trackQuantity ? (
                      <div className="rounded-lg border border-green-700/40 bg-gray-900/50 px-3 py-1.5 text-xs text-gray-400">
                        Total stock:{' '}
                        <span className="font-semibold tabular-nums text-white">{totalAvailableByVariant}</span>
                      </div>
                    ) : null}
                  </div>

                  {validationErrors.price ? (
                    <p className="mb-4 text-sm text-red-400 flex items-center bg-red-900/20 border border-red-500/30 rounded-lg px-3 py-2">
                      <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {validationErrors.price}
                    </p>
                  ) : null}

                  {validationErrors.stock && formData.trackQuantity ? (
                    <p className="mb-4 text-sm text-red-400 flex items-center bg-red-900/20 border border-red-500/30 rounded-lg px-3 py-2">
                      <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {validationErrors.stock}
                    </p>
                  ) : null}

                  <div className="overflow-x-auto rounded-lg border border-green-800/50">
                    <table className="w-full min-w-[520px] border-collapse text-sm">
                      <thead>
                        <tr className="border-b border-gray-600 bg-gray-800/90">
                          <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 sm:px-4">Product</th>
                          <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 sm:px-4">Variant</th>
                          <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 sm:px-4">Price (€)</th>
                          <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 sm:px-4">Stock</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(typePrices).flatMap(([typeKey, val]) => {
                          const label = PUBLISH_PRODUCT_LABELS[typeKey] || typeKey;
                          const isTshirt = typeKey === 'tshirt';
                          const hasQuality = isTshirt && typeof val === 'object';
                          const rowStock = typeStock[typeKey] || {
                            standard: PUBLISH_DEFAULT_STOCK,
                            premium: PUBLISH_DEFAULT_STOCK,
                            deluxe: PUBLISH_DEFAULT_STOCK,
                          };
                          const tiers = hasQuality
                            ? [
                                { key: 'standard', name: 'Standard' },
                                { key: 'premium', name: 'Premium' },
                                { key: 'deluxe', name: 'Deluxe' }
                              ]
                            : [{ key: 'standard', name: 'Standard' }];
                          return tiers.map((tier, idx) => (
                            <tr key={`${typeKey}-${tier.key}`} className="border-t border-gray-700/80 bg-gray-800/30">
                              {idx === 0 ? (
                                <td
                                  rowSpan={tiers.length}
                                  className="whitespace-nowrap px-3 py-3 align-middle text-sm font-medium text-gray-200 sm:px-4"
                                >
                                  {label}
                                </td>
                              ) : null}
                              <td className="whitespace-nowrap px-3 py-3 align-middle text-gray-400 sm:px-4">{tier.name}</td>
                              <td className="min-w-[9rem] px-3 py-2 align-middle sm:px-4">
                                <div className="relative max-w-[11rem]">
                                  <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">€</span>
                                  <input
                                    type="number"
                                    value={hasQuality ? (val[tier.key] ?? PUBLISH_DEFAULT_PRICE) : val}
                                    onChange={(e) => {
                                      setValidationErrors(prev => ({ ...prev, price: '' }));
                                      if (hasQuality) {
                                        setTypePrices((prev) => ({
                                          ...prev,
                                          [typeKey]: { ...prev[typeKey], [tier.key]: e.target.value }
                                        }));
                                      } else {
                                        setTypePrices((prev) => ({ ...prev, [typeKey]: e.target.value }));
                                      }
                                    }}
                                    step="0.01"
                                    min="0"
                                    className="w-full rounded-lg border border-gray-600 bg-gray-700 py-2 pl-7 pr-2 text-sm font-semibold text-white focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/25"
                                  />
                                </div>
                              </td>
                              <td className="min-w-[8rem] px-3 py-2 align-middle sm:px-4">
                                <input
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={rowStock[tier.key] ?? PUBLISH_DEFAULT_STOCK}
                                  disabled={!formData.trackQuantity}
                                  onChange={(e) => {
                                    setValidationErrors((prev) => ({ ...prev, stock: '' }));
                                    const digits = e.target.value.replace(/\D/g, '');
                                    setTypeStock((prev) => ({
                                      ...prev,
                                      [typeKey]: {
                                        standard: '0',
                                        premium: '0',
                                        deluxe: '0',
                                        ...prev[typeKey],
                                        [tier.key]: digits === '' ? '0' : digits
                                      }
                                    }));
                                  }}
                                  className={`w-full max-w-[9rem] rounded-lg border px-2 py-2 text-sm text-white focus:outline-none focus:ring-2 ${
                                    formData.trackQuantity
                                      ? 'border-gray-600 bg-gray-700 focus:border-blue-500 focus:ring-blue-500/25'
                                      : 'cursor-not-allowed border-gray-700 bg-gray-800/50 text-gray-500'
                                  }`}
                                />
                              </td>
                            </tr>
                          ));
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-6">
                    <label className="flex items-center space-x-3 cursor-pointer group">
                      <div className="relative">
                        <input
                          type="checkbox"
                          name="chargeTax"
                          checked={formData.chargeTax}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <div className={`w-6 h-6 rounded-lg border-2 transition-all duration-200 ${formData.chargeTax ? 'bg-green-500 border-green-500' : 'border-gray-500 group-hover:border-green-400'}`}>
                          {formData.chargeTax && (
                            <svg className="w-4 h-4 text-white absolute top-0.5 left-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                      <span className="text-sm font-medium text-gray-300">Charge tax on this product</span>
                    </label>
                  </div>
                </div>

                {/* Inventory Card */}
                <div className="bg-gradient-to-br from-blue-900/50 to-indigo-900/50 border border-blue-700/50 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-blue-600/20 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-white">Inventory</h3>
                  </div>
                  
                  <div className="space-y-6">
                    <label className="flex items-center space-x-3 cursor-pointer group">
                      <div className="relative">
                        <input
                          type="checkbox"
                          name="trackQuantity"
                          checked={formData.trackQuantity}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <div className={`w-6 h-6 rounded-lg border-2 transition-all duration-200 ${formData.trackQuantity ? 'bg-blue-500 border-blue-500' : 'border-gray-500 group-hover:border-blue-400'}`}>
                          {formData.trackQuantity && (
                            <svg className="w-4 h-4 text-white absolute top-0.5 left-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-gray-300">Track quantity</span>
                    </label>

                    {formData.trackQuantity ? (
                      <p className="text-sm text-gray-400 rounded-lg border border-blue-800/40 bg-gray-900/40 px-3 py-2">
                        Set stock per variant in the <span className="font-medium text-gray-300">Pricing &amp; inventory</span> table above.
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500">Enable tracking to assign stock levels per variant.</p>
                    )}

                    <label className="flex items-center space-x-3 cursor-pointer group">
                      <div className="relative">
                        <input
                          type="checkbox"
                          name="continueSellingOutOfStock"
                          checked={formData.continueSellingOutOfStock}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <div className={`w-6 h-6 rounded-lg border-2 transition-all duration-200 ${formData.continueSellingOutOfStock ? 'bg-blue-500 border-blue-500' : 'border-gray-500 group-hover:border-blue-400'}`}>
                          {formData.continueSellingOutOfStock && (
                            <svg className="w-4 h-4 text-white absolute top-0.5 left-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-gray-300">Continue selling when out of stock</span>
                    </label>
                  </div>
                </div>

                {/* Shipping Card */}
                <div className="bg-gradient-to-br from-orange-900/50 to-amber-900/50 border border-orange-700/50 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-orange-600/20 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-white">Shipping</h3>
                  </div>
                  
                  <div className="space-y-6">
                    <label className="flex items-center space-x-3 cursor-pointer group">
                      <div className="relative">
                        <input
                          type="checkbox"
                          name="isPhysicalProduct"
                          checked={formData.isPhysicalProduct}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <div className={`w-6 h-6 rounded-lg border-2 transition-all duration-200 ${formData.isPhysicalProduct ? 'bg-orange-500 border-orange-500' : 'border-gray-500 group-hover:border-orange-400'}`}>
                          {formData.isPhysicalProduct && (
                            <svg className="w-4 h-4 text-white absolute top-0.5 left-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-gray-300">This is a physical product</span>
                    </label>
                    
                    {formData.isPhysicalProduct && (
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-300 mb-3">
                            Weight
                          </label>
                          <input
                            type="number"
                            name="weight"
                            value={formData.weight}
                            onChange={handleInputChange}
                            step="0.1"
                            min="0"
                            className="w-full px-4 py-3 border border-gray-600 rounded-xl focus:outline-none focus:ring-3 focus:ring-orange-500/20 focus:border-orange-500 text-white bg-gray-800 transition-all duration-200"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-300 mb-3">
                            Unit
                          </label>
                          <select
                            name="weightUnit"
                            value={formData.weightUnit}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-600 rounded-xl focus:outline-none focus:ring-3 focus:ring-orange-500/20 focus:border-orange-500 text-white bg-gray-800 transition-all duration-200"
                          >
                            <option value="kg" className="text-white bg-gray-800">kg</option>
                            <option value="g" className="text-white bg-gray-800">g</option>
                            <option value="lb" className="text-white bg-gray-800">lb</option>
                            <option value="oz" className="text-white bg-gray-800">oz</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column - Product Organization */}
              <div className="space-y-8">
                {/* Status Card */}
                <div className="bg-gray-700 border border-gray-600 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-gray-600/20 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-white">Status</h3>
                  </div>
                  
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-600 rounded-xl focus:outline-none focus:ring-3 focus:ring-pink-500/20 focus:border-pink-500 text-white bg-gray-800 transition-all duration-200 font-medium"
                  >
                    <option value="active" className="text-white bg-gray-800">Active</option>
                    <option value="draft" className="text-white bg-gray-800">Draft</option>
                    <option value="archived" className="text-white bg-gray-800">Archived</option>
                  </select>
                </div>

                {/* Product Organization Card */}
                <div className="bg-gradient-to-br from-purple-900/50 to-pink-900/50 border border-purple-700/50 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-purple-600/20 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white">Product Organization</h3>
                      <p className="text-sm text-gray-400">Categorize and organize your product</p>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-500 mb-3">
                        Product Type
                      </label>
                      <div className="w-full px-4 py-3 border border-gray-600 rounded-xl bg-gray-900 transition-all duration-200">
                        <div className="flex flex-wrap gap-1.5">
                          {(() => {
                            const labels = { tshirt: '👕 T-Shirt', mug: '☕ Mug', poster: '🖼️ Poster', totebag: '👜 Tote Bag', casquette: '🧢 Cap' };
                            const products = upload?.products;
                            const isAll = !products || typeof products !== 'object' || Object.keys(products).length === 0 || Object.values(products).every(v => v);
                            const items = isAll ? Object.entries(labels) : Object.entries(labels).filter(([key]) => products[key] === true);
                            return items.map(([key, label]) => (
                              <span key={key} className="text-xs bg-cyan-600/30 text-cyan-300 border border-cyan-500/30 px-2.5 py-1 rounded-full font-medium">
                                {label}
                              </span>
                            ));
                          })()}
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-500 mb-3">
                        Vendor <span className="text-gray-600">(Streamer's site)</span>
                      </label>
                      <input
                        type="text"
                        name="vendor"
                        value={formData.vendor}
                        onChange={handleInputChange}
                        disabled
                        className="w-full px-4 py-3 border border-gray-600 rounded-xl text-gray-400 bg-gray-900 cursor-not-allowed transition-all duration-200 opacity-60"
                        placeholder="Loaded from submission data"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-500 mb-3">
                        Tags 
                      </label>
                      <input
                        type="text"
                        name="tags"
                        value={formData.tags}
                        onChange={handleInputChange}
                        disabled
                        className="w-full px-4 py-3 border border-gray-600 rounded-xl text-gray-400 bg-gray-900 cursor-not-allowed transition-all duration-200 opacity-60"
                        placeholder="Loaded from submission data"
                      />
                      <p className="text-xs text-gray-500 mt-2">Separate tags with commas</p>
                    </div>
                  </div>
                </div>

                {/* SEO Card */}
                <div className="bg-gray-700 border border-gray-600 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-gray-600/20 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-white">SEO & SKU</h3>
                  </div>
                  
                  <div className="space-y-6">
                    <label className="flex items-center space-x-3 cursor-pointer group">
                      <div className="relative">
                        <input
                          type="checkbox"
                          name="hasSKU"
                          checked={formData.hasSKU}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <div className={`w-6 h-6 rounded-lg border-2 transition-all duration-200 ${formData.hasSKU ? 'bg-gray-500 border-gray-500' : 'border-gray-500 group-hover:border-gray-400'}`}>
                          {formData.hasSKU && (
                            <svg className="w-4 h-4 text-white absolute top-0.5 left-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-gray-300">Track with SKU</span>
                    </label>
                    
                    {formData.hasSKU && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-300 mb-3">
                            SKU (Stock Keeping Unit)
                          </label>
                          <input
                            type="text"
                            name="sku"
                            value={formData.sku}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-600 rounded-xl focus:outline-none focus:ring-3 focus:ring-gray-500/20 focus:border-gray-500 text-white bg-gray-800 transition-all duration-200"
                            placeholder="e.g., TSH-001-RED-M"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-semibold text-gray-300 mb-3">
                            Barcode (ISBN, UPC, GTIN, etc.)
                          </label>
                          <input
                            type="text"
                            name="barcode"
                            value={formData.barcode}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-600 rounded-xl focus:outline-none focus:ring-3 focus:ring-gray-500/20 focus:border-gray-500 text-white bg-gray-800 transition-all duration-200"
                            placeholder="e.g., 123456789012"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Required fields note */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-400">* Required fields</p>
            </div>

            {/* Modal Actions */}
            <div className="flex justify-end space-x-4 mt-6 pt-6 border-t border-gray-600">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isPublishing}
                className="px-6 py-3 bg-pink-600 hover:bg-pink-700 disabled:bg-gray-500 text-white rounded-lg font-medium transition-colors flex items-center space-x-2"
              >
                {isPublishing ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    <span>Creating Product...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-check"></i>
                    <span>Create Product</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Image Popup Modal Component
const ImageModal = ({ isOpen, imageUrl, onClose, blurReason, revealBlurred, onReveal, t }) => {
  if (!isOpen || !imageUrl) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-90 z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="relative max-w-4xl max-h-[90vh] w-full h-full flex items-center justify-center">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 bg-gray-800 hover:bg-gray-700 text-white p-2 rounded-full transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        {/* Image Container */}
        <div className="relative" onClick={(e) => e.stopPropagation()}>
          {/* Image */}
          <img 
            src={imageUrl} 
            alt="Enlarged view" 
            className={`max-w-full max-h-full object-contain rounded-lg shadow-2xl ${blurReason && !revealBlurred ? 'blur-xl' : ''}`}
          />
          
          {/* View Image Button - Only show for blurred images that haven't been revealed */}
          {blurReason && !revealBlurred && (
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <button
                onClick={onReveal}
                className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-2xl transition-all transform hover:scale-105 flex items-center space-x-3"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <span>{t ? t('viewImage') : 'View Image'}</span>
              </button>
              <p className="mt-4 text-red-400 text-sm max-w-md text-center px-4">
                <i className="fas fa-exclamation-triangle mr-2"></i>
                {blurReason}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 